import { useState, useEffect, useCallback, useRef, useMemo } from "react";

// ═══════════════════════════════════════════════════════════════
//  LOGIQUE MÉTIER (inchangée)
// ═══════════════════════════════════════════════════════════════
const DAYS_SHORT = ["Lun", "Mar", "Mer", "Jeu", "Ven"];
const MONTHS = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];
const EMP_COLORS = [
  "#34d399",
  "#60a5fa",
  "#f87171",
  "#fbbf24",
  "#a78bfa",
  "#f472b6",
  "#fb923c",
  "#22d3ee",
  "#86efac",
  "#fde68a",
];
const ROLES = [
  "Dev",
  "Design",
  "RH",
  "Marketing",
  "Finance",
  "Support",
  "Ops",
  "QA",
  "Data",
  "Produit",
];
const STATUS_CYCLE = { present: "remote", remote: "absent", absent: "present" };

function getFrenchHolidays(year) {
  const h = new Set();
  const add = (m, d) =>
    h.add(
      `${year}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`
    );
  add(1, 1);
  add(5, 1);
  add(5, 8);
  add(7, 14);
  add(8, 15);
  add(11, 1);
  add(11, 11);
  add(12, 25);
  const a = year % 19,
    b = Math.floor(year / 100),
    c = year % 100,
    d_ = Math.floor(b / 4),
    e = b % 4,
    f = Math.floor((b + 8) / 25),
    g = Math.floor((b - f + 1) / 3),
    h_ = (19 * a + b - d_ - g + 15) % 30,
    i = Math.floor(c / 4),
    k = c % 4,
    l = (32 + 2 * e + 2 * i - h_ - k) % 7,
    m_ = Math.floor((a + 11 * h_ + 22 * l) / 451),
    mo = Math.floor((h_ + l - 7 * m_ + 114) / 31),
    dy = ((h_ + l - 7 * m_ + 114) % 31) + 1;
  add(mo, dy);
  add(mo, dy + 1);
  const asc = new Date(year, mo - 1, dy + 39);
  add(asc.getMonth() + 1, asc.getDate());
  const pent = new Date(year, mo - 1, dy + 50);
  add(pent.getMonth() + 1, pent.getDate());
  return h;
}

function getWeeksInMonth(year, month, holidays) {
  const weeks = [];
  const d = new Date(year, month, 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  let wn = 1;
  while (d.getMonth() === month) {
    const week = [];
    const start = new Date(d);
    for (let i = 0; i < 5; i++) {
      if (d.getMonth() === month) {
        const ds = d.toISOString().split("T")[0];
        week.push({
          date: new Date(d),
          dateStr: ds,
          isHoliday: holidays?.has(ds) || false,
        });
      }
      d.setDate(d.getDate() + 1);
    }
    if (week.length) weeks.push({ week: wn++, days: week, start });
    while (d.getDay() === 6 || d.getDay() === 0) d.setDate(d.getDate() + 1);
  }
  return weeks;
}

function generateSchedule(employees, filters, weeks, overrides = {}) {
  const schedule = {};
  const { rotation, changeFrequency, remoteManagement, absenceRate } = filters;
  weeks.forEach((week, wi) => {
    schedule[week.week] = {};
    week.days.forEach(({ dateStr, isHoliday }, di) => {
      schedule[week.week][dateStr] = [];
      employees.forEach((emp) => {
        if (isHoliday) {
          schedule[week.week][dateStr].push({
            ...emp,
            am: "holiday",
            pm: "holiday",
            amManual: false,
            pmManual: false,
          });
          return;
        }
        const ovAM = overrides[`${emp.id}_${dateStr}_am`],
          ovPM = overrides[`${emp.id}_${dateStr}_pm`];
        const isAbsent = Math.random() < absenceRate / 100;
        let slot = "P";
        if (rotation === "matin-soir") {
          const sh = (emp.id + wi + Math.floor(di / changeFrequency)) % 2;
          slot = sh === 0 ? "M" : "S";
        } else if (rotation === "semaine")
          slot = (emp.id + wi) % 2 === 0 ? "G1" : "G2";
        let remotePart = "none";
        if (remoteManagement && emp.remote && !isAbsent) {
          const rd = changeFrequency >= 3 ? [1, 3] : [2];
          if (rd.includes(di))
            remotePart = (emp.id + wi) % 2 === 0 ? "am" : "pm";
        }
        const baseAM = isAbsent
          ? "absent"
          : remotePart === "am"
          ? "remote"
          : "present";
        const basePM = isAbsent
          ? "absent"
          : remotePart === "pm"
          ? "remote"
          : "present";
        schedule[week.week][dateStr].push({
          ...emp,
          slot,
          am: ovAM || baseAM,
          pm: ovPM || basePM,
          amManual: !!ovAM,
          pmManual: !!ovPM,
        });
      });
    });
  });
  return schedule;
}

function buildRetroTable(schedule, employees, weeks) {
  const table = {};
  employees.forEach((e) => {
    table[e.id] = {
      id: e.id,
      name: e.name,
      color: e.color,
      phone: e.phone,
      demi: 0,
      present: 0,
      remote: 0,
      absent: 0,
      holiday: 0,
      byWeek: {},
    };
  });
  weeks.forEach((week) => {
    Object.entries(schedule[week.week] || {}).forEach(([, dayArr]) => {
      dayArr.forEach((entry) => {
        if (!table[entry.id]) return;
        const r = table[entry.id];
        r.byWeek[week.week] = r.byWeek[week.week] || { p: 0, r: 0, a: 0 };
        ["am", "pm"].forEach((h) => {
          const s = entry[h];
          r.demi++;
          if (s === "absent") {
            r.absent++;
            r.byWeek[week.week].a++;
          } else if (s === "remote") {
            r.remote++;
            r.byWeek[week.week].r++;
          } else if (s === "holiday") r.holiday++;
          else {
            r.present++;
            r.byWeek[week.week].p++;
          }
        });
      });
    });
  });
  return table;
}

function computeBalanceScore(schedule, week) {
  const wd = schedule[week] || {};
  const counts = { present: 0, remote: 0, absent: 0 };
  Object.values(wd).forEach((da) =>
    da.forEach((e) =>
      ["am", "pm"].forEach((h) => {
        if (counts[e[h]] !== undefined) counts[e[h]]++;
      })
    )
  );
  const vals = Object.values(counts).filter((v) => v > 0);
  if (!vals.length) return 100;
  const mx = Math.max(...vals),
    mn = Math.min(...vals);
  return mx === 0 ? 100 : Math.round(100 - ((mx - mn) / mx) * 100);
}

function exportRetroCSV(retroTable, month, year) {
  const rows = [
    [
      "Employé",
      "Téléphone",
      "Demi-journées",
      "Présent",
      "Télétravail",
      "Absent",
      "Taux %",
    ],
  ];
  Object.values(retroTable).forEach((r) => {
    const rate =
      r.demi > 0 ? Math.round(((r.present + r.remote) / r.demi) * 100) : 0;
    rows.push([
      r.name,
      r.phone ? "Oui" : "Non",
      r.demi,
      r.present,
      r.remote,
      r.absent,
      rate,
    ]);
  });
  const csv = rows.map((r) => r.join(";")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `planning_${MONTHS[month]}_${year}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

async function loadJsPDF() {
  if (window.jspdf) return window.jspdf.jsPDF;
  await new Promise((res, rej) => {
    const s = document.createElement("script");
    s.src =
      "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
    s.onload = res;
    s.onerror = rej;
    document.head.appendChild(s);
  });
  await new Promise((res, rej) => {
    const s = document.createElement("script");
    s.src =
      "https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js";
    s.onload = res;
    s.onerror = rej;
    document.head.appendChild(s);
  });
  return window.jspdf.jsPDF;
}
function hexToRgb(hex) {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

async function exportPlanningPDF({
  employees,
  schedule,
  weeks,
  filters,
  month,
  year,
  MONTHS,
  DAYS_SHORT,
}) {
  const JsPDF = await loadJsPDF();
  const doc = new JsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth(),
    H = doc.internal.pageSize.getHeight();
  const DARK = [8, 10, 20],
    PANEL = [14, 20, 38],
    BORDER = [30, 40, 60],
    ACCENT = [99, 102, 241],
    WHITE = [241, 245, 249],
    MUTED = [71, 85, 105];
  const ST = {
    present: { bg: [20, 80, 50], fg: [134, 239, 172], lbl: "P" },
    remote: { bg: [20, 40, 80], fg: [147, 197, 253], lbl: "TT" },
    absent: { bg: [80, 20, 20], fg: [252, 165, 165], lbl: "ABS" },
    holiday: { bg: [80, 70, 10], fg: [253, 230, 138], lbl: "FER" },
  };
  const base = (title, pn, tp) => {
    doc.setFillColor(...DARK);
    doc.rect(0, 0, W, H, "F");
    doc.setFillColor(...PANEL);
    doc.rect(0, 0, W, 18, "F");
    doc.setFillColor(...ACCENT);
    doc.rect(0, 0, W, 1.5, "F");
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.3);
    doc.line(0, 18, W, 18);
    doc.setTextColor(...ACCENT);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("◈ PLANNING AGENT", 10, 11);
    doc.setTextColor(...MUTED);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.text(title, 65, 11);
    doc.text(
      `${MONTHS[month]} ${year} · ${new Date().toLocaleDateString("fr-FR")}`,
      W - 10,
      11,
      { align: "right" }
    );
    doc.setDrawColor(...BORDER);
    doc.line(0, H - 8, W, H - 8);
    doc.setTextColor(...MUTED);
    doc.setFontSize(6);
    doc.text(`${pn}/${tp}`, W - 10, H - 4, { align: "right" });
  };
  const total = weeks.length + 1;
  base("Aperçu", 1, total);
  doc.setTextColor(...ACCENT);
  doc.setFontSize(32);
  doc.setFont("helvetica", "bold");
  doc.text("◈", W / 2, H / 2 - 25, { align: "center" });
  doc.setFontSize(18);
  doc.text("PLANNING D'ENTREPRISE", W / 2, H / 2 - 6, { align: "center" });
  doc.setFontSize(12);
  doc.setTextColor(...WHITE);
  doc.text(`${MONTHS[month]} ${year}`, W / 2, H / 2 + 7, { align: "center" });
  const rl = {
    aucune: "Aucun",
    "matin-soir": "Matin/Soir",
    semaine: "Par semaine",
  };
  [
    `${employees.length} employés`,
    `Roulement: ${rl[filters.rotation] || filters.rotation}`,
    `Absence: ${filters.absenceRate}%`,
    `Télétravail: ${filters.remoteManagement ? "Activé" : "Désactivé"}`,
    `Grille AM/PM`,
  ].forEach((l, i) => {
    doc.setFillColor(...PANEL);
    doc.roundedRect(W / 2 - 45, H / 2 + 16 + i * 9, 90, 7, 1, 1, "F");
    doc.setTextColor(...WHITE);
    doc.setFontSize(7);
    doc.text(l, W / 2, H / 2 + 20.5 + i * 9, { align: "center" });
  });
  weeks.forEach((week, wi) => {
    doc.addPage();
    base(`Semaine ${week.week}`, wi + 2, total);
    const wd = schedule[week.week] || {};
    const SY = 22,
      RH = 10,
      NW = 40,
      HW = 12,
      TX = 10;
    const tableW = NW + week.days.length * (HW * 2);
    doc.setFillColor(...PANEL);
    doc.rect(TX, SY, tableW, RH, "F");
    doc.setTextColor(...MUTED);
    doc.setFontSize(6.5);
    doc.setFont("helvetica", "bold");
    doc.text("EMPLOYÉ", TX + 4, SY + 6);
    week.days.forEach(({ date, isHoliday }, di) => {
      const dx = TX + NW + di * (HW * 2);
      if (isHoliday) {
        doc.setFillColor(80, 70, 10);
        doc.rect(dx, SY, HW * 2, RH, "F");
      }
      doc.setTextColor(...(isHoliday ? [253, 230, 138] : MUTED));
      doc.setFontSize(6.5);
      doc.setFont("helvetica", "bold");
      doc.text(DAYS_SHORT[di], dx + HW, SY + 4, { align: "center" });
      doc.setFontSize(5);
      doc.setFont("helvetica", "normal");
      doc.text(`${date.getDate()}/${date.getMonth() + 1}`, dx + HW, SY + 7, {
        align: "center",
      });
      doc.setTextColor(...MUTED);
      doc.setFontSize(4.5);
      doc.text("M", dx + HW / 2, SY + 9.5, { align: "center" });
      doc.text("PM", dx + HW + HW / 2, SY + 9.5, { align: "center" });
      doc.setDrawColor(...BORDER);
      doc.setLineWidth(0.12);
      doc.line(dx + HW, SY + 1, dx + HW, SY + RH);
    });
    employees.forEach((emp, ei) => {
      const y = SY + RH * (ei + 1);
      doc.setFillColor(...(ei % 2 === 0 ? DARK : PANEL));
      doc.rect(TX, y, tableW, RH, "F");
      const [er, eg, eb] = hexToRgb(emp.color);
      doc.setFillColor(er, eg, eb);
      doc.rect(TX, y, 2, RH, "F");
      doc.setFillColor(er, eg, eb);
      doc.circle(TX + 7, y + RH / 2, 2.8, "F");
      doc.setTextColor(8, 10, 20);
      doc.setFontSize(5);
      doc.setFont("helvetica", "bold");
      doc.text(
        emp.name
          .split(" ")
          .map((n) => n[0])
          .join(""),
        TX + 7,
        y + RH / 2 + 1,
        { align: "center" }
      );
      doc.setTextColor(...WHITE);
      doc.setFontSize(6.5);
      doc.setFont("helvetica", "bold");
      doc.text(emp.name.split(" ")[0], TX + 12, y + 4.5);
      doc.setTextColor(...(emp.phone ? [134, 239, 172] : [100, 116, 139]));
      doc.setFontSize(5);
      doc.setFont("helvetica", "normal");
      doc.text(emp.phone ? "TEL ✓" : "TEL ✗", TX + 12, y + 8.5);
      week.days.forEach(({ dateStr }, di) => {
        const entry = (wd[dateStr] || []).find((e) => e.id === emp.id);
        if (!entry) return;
        ["am", "pm"].forEach((half, hi) => {
          const s = entry[half];
          const cfg = ST[s] || ST.present;
          const cx = TX + NW + di * (HW * 2) + hi * HW + HW / 2;
          doc.setFillColor(...cfg.bg);
          doc.roundedRect(cx - 4.5, y + 1.5, 9, RH - 3, 1, 1, "F");
          doc.setTextColor(...cfg.fg);
          doc.setFontSize(5.5);
          doc.setFont("helvetica", "bold");
          doc.text(cfg.lbl, cx, y + RH / 2 + 1, { align: "center" });
        });
        doc.setDrawColor(...BORDER);
        doc.setLineWidth(0.1);
        doc.line(
          TX + NW + di * (HW * 2) + HW,
          y,
          TX + NW + di * (HW * 2) + HW,
          y + RH
        );
      });
      doc.setDrawColor(...BORDER);
      doc.setLineWidth(0.1);
      doc.line(TX, y + RH, TX + tableW, y + RH);
    });
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.25);
    doc.rect(TX, SY, tableW, RH * (employees.length + 1), "S");
    const all = Object.values(wd).flatMap((a) =>
      a.flatMap((e) => [e.am, e.pm])
    );
    const sy2 = SY + RH * (employees.length + 1) + 5;
    [
      {
        lbl: "PRÉSENT",
        val: all.filter((s) => s === "present").length,
        c: [34, 197, 94],
      },
      {
        lbl: "TÉLÉTRAVAIL",
        val: all.filter((s) => s === "remote").length,
        c: [96, 165, 250],
      },
      {
        lbl: "ABSENT",
        val: all.filter((s) => s === "absent").length,
        c: [248, 113, 113],
      },
      {
        lbl: "FÉRIÉ",
        val: all.filter((s) => s === "holiday").length,
        c: [251, 191, 36],
      },
    ].forEach((s, i) => {
      doc.setFillColor(...PANEL);
      doc.roundedRect(TX + i * 50, sy2, 46, 13, 2, 2, "F");
      doc.setFillColor(...s.c);
      doc.roundedRect(TX + i * 50, sy2, 2, 13, 1, 1, "F");
      doc.setTextColor(...s.c);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(String(s.val), TX + i * 50 + 23, sy2 + 8, { align: "center" });
      doc.setTextColor(...MUTED);
      doc.setFontSize(5);
      doc.setFont("helvetica", "normal");
      doc.text(s.lbl, TX + i * 50 + 23, sy2 + 12, { align: "center" });
    });
  });
  doc.save(`planning_${MONTHS[month]}_${year}.pdf`);
}

async function exportRetroPDF({
  retroTable,
  schedule,
  weeks,
  month,
  year,
  employees,
  MONTHS,
}) {
  const JsPDF = await loadJsPDF();
  const doc = new JsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth(),
    H = doc.internal.pageSize.getHeight();
  const DARK = [8, 10, 20],
    PANEL = [14, 20, 38],
    BORDER = [30, 40, 60],
    ACCENT = [99, 102, 241],
    WHITE = [241, 245, 249],
    MUTED = [71, 85, 105];
  doc.setFillColor(...DARK);
  doc.rect(0, 0, W, H, "F");
  doc.setFillColor(...PANEL);
  doc.rect(0, 0, W, 18, "F");
  doc.setFillColor(...ACCENT);
  doc.rect(0, 0, W, 1.5, "F");
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.line(0, 18, W, 18);
  doc.setTextColor(...ACCENT);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("◈ RÉTROACTIF", 10, 11);
  doc.setTextColor(...MUTED);
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.text(
    `${MONTHS[month]} ${year} · demi-journées · ${new Date().toLocaleDateString(
      "fr-FR"
    )}`,
    W - 10,
    11,
    { align: "right" }
  );
  doc.setDrawColor(...BORDER);
  doc.line(0, H - 8, W, H - 8);
  doc.setTextColor(...MUTED);
  doc.setFontSize(6);
  doc.text("1/1", W - 10, H - 4, { align: "right" });
  const rows = Object.values(retroTable);
  const body = rows.map((r) => {
    const rate =
      r.demi > 0 ? Math.round(((r.present + r.remote) / r.demi) * 100) : 0;
    return [
      r.name,
      r.phone ? "✓" : "✗",
      r.demi,
      r.present,
      r.remote,
      r.absent,
      `${rate}%`,
      ...weeks.map((w) => {
        const wb = r.byWeek[w.week] || { p: 0, r: 0, a: 0 };
        return `${wb.p}/${wb.r}/${wb.a}`;
      }),
    ];
  });
  const tP = rows.reduce((s, r) => s + r.present, 0),
    tR = rows.reduce((s, r) => s + r.remote, 0),
    tA = rows.reduce((s, r) => s + r.absent, 0),
    tD = rows.reduce((s, r) => s + r.demi, 0),
    avg = Math.round(
      (rows.reduce(
        (s, r) => s + (r.demi > 0 ? (r.present + r.remote) / r.demi : 0),
        0
      ) /
        Math.max(rows.length, 1)) *
        100
    );
  body.push(["TOTAUX", "", tD, tP, tR, tA, `${avg}%`, ...weeks.map(() => "")]);
  doc.autoTable({
    startY: 22,
    head: [
      [
        "Employé",
        "📞",
        "Demi-j",
        "Présent",
        "Télétravail",
        "Absent",
        "Taux",
        ...weeks.map((w) => `S${w.week}`),
      ],
    ],
    body,
    theme: "plain",
    styles: {
      font: "helvetica",
      fontSize: 8,
      textColor: WHITE,
      cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
      lineColor: BORDER,
      lineWidth: 0.15,
    },
    headStyles: {
      fillColor: PANEL,
      textColor: MUTED,
      fontStyle: "bold",
      fontSize: 7,
      halign: "center",
    },
    columnStyles: {
      0: { halign: "left", fontStyle: "bold", cellWidth: 36 },
      1: { halign: "center", cellWidth: 14 },
      2: { halign: "center", cellWidth: 13 },
      3: { halign: "center", cellWidth: 16, textColor: [134, 239, 172] },
      4: { halign: "center", cellWidth: 18, textColor: [147, 197, 253] },
      5: { halign: "center", cellWidth: 13, textColor: [252, 165, 165] },
      6: { halign: "center", cellWidth: 18 },
    },
    alternateRowStyles: { fillColor: [14, 20, 38] },
    bodyStyles: { fillColor: DARK },
    didParseCell(d) {
      if (d.row.index === body.length - 1) {
        d.cell.styles.fillColor = [30, 40, 60];
        d.cell.styles.fontStyle = "bold";
        d.cell.styles.textColor = [167, 178, 254];
      }
      if (
        d.column.index === 6 &&
        d.section === "body" &&
        d.row.index < body.length - 1
      ) {
        const v = parseInt(d.cell.text[0]);
        d.cell.styles.textColor =
          v >= 80 ? [34, 197, 94] : v >= 60 ? [245, 158, 11] : [239, 68, 68];
        d.cell.styles.fontStyle = "bold";
      }
    },
    margin: { left: 10, right: 10 },
  });
  const gy = doc.lastAutoTable.finalY + 8;
  if (gy < H - 22) {
    doc.setTextColor(...MUTED);
    doc.setFontSize(6);
    doc.setFont("helvetica", "bold");
    doc.text("PRÉSENCE PAR SEMAINE", 10, gy);
    const bw = Math.min(20, (W - 20) / Math.max(weeks.length, 1) - 2);
    weeks.forEach((wk, i) => {
      const all = Object.values(schedule[wk.week] || {})
        .flatMap((a) => a.flatMap((e) => [e.am, e.pm]))
        .filter((s) => s !== "holiday");
      const pct =
        all.length > 0
          ? all.filter((s) => s !== "absent").length / all.length
          : 0;
      const bh = Math.max(2, pct * 15),
        bx = 10 + i * (bw + 3),
        by = gy + 18 - bh;
      const col =
        pct >= 0.8
          ? [34, 197, 94]
          : pct >= 0.6
          ? [245, 158, 11]
          : [239, 68, 68];
      doc.setFillColor(...col);
      doc.roundedRect(bx, by, bw, bh, 1, 1, "F");
      doc.setTextColor(...MUTED);
      doc.setFontSize(5);
      doc.text(`${Math.round(pct * 100)}%`, bx + bw / 2, by - 1, {
        align: "center",
      });
      doc.text(`S${wk.week}`, bx + bw / 2, gy + 21, { align: "center" });
    });
  }
  doc.save(`retroactif_${MONTHS[month]}_${year}.pdf`);
}

async function callAIAgent(messages) {
  const system = `Tu es un agent IA expert RH en plannings d'entreprise (grille matin/après-midi).
Réponds en JSON strict: {"message":"réponse utile fr (2-4 phrases)","suggestion":"action concrète ou null","alert":"alerte critique ou null","action":"regenerate|export|null"}
Détecte: déséquilibres AM/PM, surcharge télétravail, absences critiques.`;
  const res = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-sonnet-4-20250514",
      max_tokens: 1000,
      system,
      messages,
    }),
  });
  const data = await res.json();
  const text = data.content?.[0]?.text || "{}";
  try {
    return JSON.parse(text.replace(/```json|```/g, "").trim());
  } catch {
    return { message: text, suggestion: null, alert: null, action: null };
  }
}

// ═══════════════════════════════════════════════════════════════
//  DESIGN — OBSIDIAN & COPPER
//  Typo: Bricolage Grotesque (display) + Plus Jakarta Sans (corps)
//  Palette: ardoise nuit, cuivre/ambre chaud, jade, ivoire
// ═══════════════════════════════════════════════════════════════
const T = {
  bg: "#0a0b0f",
  bgDeep: "#06070b",
  surface: "rgba(16,18,26,0.92)",
  surfaceHi: "rgba(22,25,38,0.95)",
  surfaceLow: "rgba(12,13,20,0.7)",
  border: "rgba(200,160,100,0.12)",
  borderHi: "rgba(200,160,100,0.28)",
  borderMid: "rgba(200,160,100,0.18)",
  // Typographie — ivoire chaud
  text: "#f0ede6",
  textSub: "#b8b0a0",
  textMuted: "#6b6358",
  textDim: "#2e2b26",
  // Accents cuivre & complémentaires
  copper: "#c8a064", // cuivre chaud principal
  copperLight: "#e8c88a", // cuivre clair
  copperDim: "rgba(200,160,100,0.15)",
  jade: "#4ecdc4", // jade aqua froid
  jadeDim: "rgba(78,205,196,0.12)",
  rose: "#e8758a",
  roseDim: "rgba(232,117,138,0.12)",
  amber: "#f0b840",
  amberDim: "rgba(240,184,64,0.12)",
  // Status
  statusPresent: {
    bg: "rgba(78,205,196,0.1)",
    border: "rgba(78,205,196,0.28)",
    text: "#4ecdc4",
    glow: "#4ecdc430",
  },
  statusRemote: {
    bg: "rgba(200,160,100,0.1)",
    border: "rgba(200,160,100,0.28)",
    text: "#c8a064",
    glow: "#c8a06430",
  },
  statusAbsent: {
    bg: "rgba(232,117,138,0.1)",
    border: "rgba(232,117,138,0.28)",
    text: "#e8758a",
    glow: "#e8758a30",
  },
  statusHoliday: {
    bg: "rgba(240,184,64,0.1)",
    border: "rgba(240,184,64,0.28)",
    text: "#f0b840",
    glow: "#f0b84030",
  },
  // Polices
  fontDisplay: "'Bricolage Grotesque','Plus Jakarta Sans',sans-serif",
  fontBody: "'Plus Jakarta Sans',sans-serif",
};

// ═══════════════════════════════════════════════════════════════
//  COMPOSANTS
// ═══════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════
//  COMPOSANTS UI
// ═══════════════════════════════════════════════════════════════

function Card({ children, style = {}, glow = "", hover = false }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => hover && setHov(true)}
      onMouseLeave={() => hover && setHov(false)}
      style={{
        background: T.surface,
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        borderRadius: 20,
        border: `1px solid ${hov ? T.borderMid : T.border}`,
        boxShadow: glow
          ? `0 0 60px ${glow}14, 0 12px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(200,160,100,0.06)`
          : `0 4px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(200,160,100,0.04)`,
        transition: "border 0.25s, box-shadow 0.25s",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// Titre de section avec ornement lateral
function SLabel({ children, color }) {
  const c = color || T.copper;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        marginBottom: 14,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <div style={{ width: 2, height: 10, background: c, borderRadius: 1 }} />
        <div
          style={{ width: 2, height: 4, background: `${c}50`, borderRadius: 1 }}
        />
      </div>
      <span
        style={{
          fontFamily: T.fontDisplay,
          fontSize: 10,
          fontWeight: 700,
          color: c,
          letterSpacing: "0.15em",
          textTransform: "uppercase",
        }}
      >
        {children}
      </span>
    </div>
  );
}

// Badge demi-journée
function HalfBadge({ status, half, manual, onClick }) {
  const cfg =
    T[`status${status.charAt(0).toUpperCase() + status.slice(1)}`] ||
    T.statusPresent;
  const syms = { present: "◆", remote: "⌂", absent: "✕", holiday: "✦" };
  const halfLabel = half === "am" ? "M" : "S";
  return (
    <div
      onClick={onClick}
      title={`${half === "am" ? "Matin" : "Soir"} · ${status}${
        manual ? " (modifié)" : ""
      }`}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: 28,
        height: 30,
        borderRadius: 8,
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        cursor: status === "holiday" ? "default" : "pointer",
        boxShadow: manual
          ? `0 0 12px ${cfg.glow},inset 0 0 10px ${cfg.glow}`
          : "none",
        transition: "all 0.18s cubic-bezier(.34,1.56,.64,1)",
        userSelect: "none",
        flexShrink: 0,
        position: "relative",
      }}
      onMouseEnter={(e) => {
        if (status !== "holiday") {
          e.currentTarget.style.transform = "scale(1.14) translateY(-1px)";
          e.currentTarget.style.boxShadow = `0 6px 18px ${cfg.glow}`;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1) translateY(0)";
        e.currentTarget.style.boxShadow = manual
          ? `0 0 12px ${cfg.glow}`
          : "none";
      }}
    >
      <span
        style={{
          fontSize: 8,
          fontWeight: 800,
          color: cfg.text,
          fontFamily: T.fontDisplay,
          lineHeight: 1,
        }}
      >
        {syms[status] || "◆"}
      </span>
      <span
        style={{
          fontSize: 6,
          color: `${cfg.text}80`,
          fontFamily: T.fontBody,
          fontWeight: 700,
          lineHeight: 1,
          marginTop: 2,
          letterSpacing: "0.05em",
        }}
      >
        {halfLabel}
      </span>
    </div>
  );
}

function DayCell({ entry, onClickHalf }) {
  if (!entry)
    return (
      <span style={{ color: T.textDim, fontSize: 10, fontFamily: T.fontBody }}>
        —
      </span>
    );
  return (
    <div
      style={{
        display: "flex",
        gap: 3,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <HalfBadge
        status={entry.am}
        half="am"
        manual={entry.amManual}
        onClick={() => entry.am !== "holiday" && onClickHalf("am")}
      />
      <div
        style={{
          width: 1,
          height: 16,
          background: `linear-gradient(180deg,transparent,${T.borderMid},transparent)`,
        }}
      />
      <HalfBadge
        status={entry.pm}
        half="pm"
        manual={entry.pmManual}
        onClick={() => entry.pm !== "holiday" && onClickHalf("pm")}
      />
    </div>
  );
}

function Tip({ text, children }) {
  const [show, setShow] = useState(false);
  return (
    <div
      style={{ position: "relative", display: "inline-block" }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <div
          style={{
            position: "absolute",
            bottom: "calc(100% + 9px)",
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(10,11,15,0.97)",
            backdropFilter: "blur(16px)",
            color: T.textSub,
            fontSize: 11,
            padding: "6px 13px",
            borderRadius: 9,
            whiteSpace: "nowrap",
            pointerEvents: "none",
            border: `1px solid ${T.borderMid}`,
            zIndex: 600,
            boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
            fontFamily: T.fontBody,
          }}
        >
          {text}
        </div>
      )}
    </div>
  );
}

// Score circulaire en cuivre
function BalancePill({ score }) {
  const c = score >= 80 ? T.jade : score >= 60 ? T.amber : T.rose;
  const r = 10;
  const circ = 2 * Math.PI * r;
  return (
    <Tip text={`Score d'équilibre planning: ${score}/100`}>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 9,
          cursor: "help",
          background: `${c}0e`,
          border: `1px solid ${c}35`,
          borderRadius: 50,
          padding: "5px 13px 5px 7px",
        }}
      >
        <div
          style={{ width: 30, height: 30, position: "relative", flexShrink: 0 }}
        >
          <svg
            width="30"
            height="30"
            style={{ transform: "rotate(-90deg)", display: "block" }}
          >
            <circle
              cx="15"
              cy="15"
              r={r}
              fill="none"
              stroke={`${c}18`}
              strokeWidth="2.5"
            />
            <circle
              cx="15"
              cy="15"
              r={r}
              fill="none"
              stroke={c}
              strokeWidth="2.5"
              strokeDasharray={circ}
              strokeDashoffset={circ * (1 - score / 100)}
              strokeLinecap="round"
              style={{
                transition:
                  "stroke-dashoffset 0.6s cubic-bezier(.34,1.16,.64,1)",
              }}
            />
          </svg>
          <span
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 7.5,
              fontWeight: 800,
              color: c,
              fontFamily: T.fontDisplay,
            }}
          >
            {score}
          </span>
        </div>
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: c,
              fontFamily: T.fontDisplay,
              lineHeight: 1,
            }}
          >
            Équilibre
          </div>
          <div
            style={{
              fontSize: 9,
              color: `${c}80`,
              fontFamily: T.fontBody,
              lineHeight: 1,
              marginTop: 1,
            }}
          >
            {score >= 80 ? "Excellent" : score >= 60 ? "Correct" : "À revoir"}
          </div>
        </div>
      </div>
    </Tip>
  );
}

function StatBlock({ icon, label, value, color, note }) {
  return (
    <div
      style={{
        background: `${color}08`,
        border: `1px solid ${color}20`,
        borderRadius: 18,
        padding: "18px 20px",
        position: "relative",
        overflow: "hidden",
        transition: "transform 0.2s,box-shadow 0.2s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = `0 8px 32px ${color}20`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div
        style={{
          position: "absolute",
          top: -16,
          right: -16,
          width: 72,
          height: 72,
          borderRadius: "50%",
          background: `${color}07`,
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: 3,
          height: "100%",
          background: `linear-gradient(180deg,${color},${color}00)`,
          borderRadius: "0 0 0 18px",
        }}
      />
      <div style={{ fontSize: 20, marginBottom: 8 }}>{icon}</div>
      <div
        style={{
          fontFamily: T.fontDisplay,
          fontSize: 30,
          fontWeight: 800,
          color,
          lineHeight: 1,
          letterSpacing: -1,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontFamily: T.fontBody,
          fontSize: 11,
          color: T.textMuted,
          fontWeight: 500,
          marginTop: 5,
        }}
      >
        {label}
      </div>
      {note && (
        <div
          style={{
            fontFamily: T.fontBody,
            fontSize: 9,
            color: `${color}70`,
            fontWeight: 600,
            marginTop: 3,
          }}
        >
          {note}
        </div>
      )}
    </div>
  );
}

function Toggler({ value, onChange, colorOn }) {
  const c = colorOn || T.copper;
  return (
    <div
      onClick={onChange}
      style={{
        width: 44,
        height: 24,
        borderRadius: 12,
        background: value
          ? `linear-gradient(90deg,${c},${c}bb)`
          : "rgba(30,32,44,0.9)",
        border: `1px solid ${value ? `${c}60` : T.border}`,
        position: "relative",
        cursor: "pointer",
        transition: "all 0.28s",
        boxShadow: value ? `0 0 16px ${c}30` : "none",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 4,
          left: value ? 22 : 4,
          width: 14,
          height: 14,
          borderRadius: "50%",
          background: "#f0ede6",
          transition: "left 0.28s cubic-bezier(.34,1.56,.64,1)",
          boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
        }}
      />
    </div>
  );
}

function Avatar({ name, color, size = 32 }) {
  const init = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.3,
        flexShrink: 0,
        background: `linear-gradient(135deg,${color}40,${color}18)`,
        border: `1.5px solid ${color}60`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: T.fontDisplay,
        fontSize: size * 0.31,
        fontWeight: 800,
        color,
        boxShadow: `0 2px 12px ${color}25,inset 0 1px 0 ${color}30`,
        letterSpacing: -0.5,
      }}
    >
      {init}
    </div>
  );
}

function CopperBtn({ onClick, loading, children, style = {} }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      style={{
        background: loading
          ? "rgba(16,18,26,0.6)"
          : "linear-gradient(135deg,#c8a064,#e8c88a,#c8a064)",
        backgroundSize: "200% 100%",
        border: "none",
        borderRadius: 11,
        padding: "8px 16px",
        color: loading ? "#6b6358" : "#0a0b0f",
        fontSize: 11,
        fontFamily: T.fontDisplay,
        fontWeight: 800,
        cursor: loading ? "not-allowed" : "pointer",
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        display: "flex",
        alignItems: "center",
        gap: 6,
        transition: "all 0.25s",
        boxShadow: loading ? "none" : "0 4px 20px rgba(200,160,100,0.35)",
        ...style,
      }}
      onMouseEnter={(e) => {
        if (!loading) {
          e.currentTarget.style.backgroundPosition = "100% 0";
          e.currentTarget.style.boxShadow = "0 6px 28px rgba(200,160,100,0.5)";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundPosition = "0 0";
        e.currentTarget.style.boxShadow = loading
          ? "none"
          : "0 4px 20px rgba(200,160,100,0.35)";
      }}
    >
      {loading ? (
        <>
          <span
            style={{
              animation: "spin 1s linear infinite",
              display: "inline-block",
              fontSize: 13,
            }}
          >
            ⟳
          </span>{" "}
          Export…
        </>
      ) : (
        children
      )}
    </button>
  );
}

function GhostBtn({ onClick, children, active = false, style = {} }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? `rgba(200,160,100,0.14)` : "rgba(16,18,26,0.6)",
        border: `1px solid ${active ? T.borderHi : T.border}`,
        borderRadius: 10,
        padding: "8px 18px",
        color: active ? T.copperLight : T.textMuted,
        fontSize: 12,
        fontFamily: T.fontBody,
        cursor: "pointer",
        fontWeight: 600,
        letterSpacing: 0.3,
        transition: "all 0.2s",
        backdropFilter: "blur(8px)",
        boxShadow: active ? "0 4px 20px rgba(200,160,100,0.15)" : "none",
        ...style,
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.borderColor = T.borderMid;
          e.currentTarget.style.color = T.textSub;
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.borderColor = T.border;
          e.currentTarget.style.color = T.textMuted;
        }
      }}
    >
      {children}
    </button>
  );
}

// Modal employé
function EmployeeModal({ employee, onSave, onClose }) {
  const [form, setForm] = useState(
    employee || {
      name: "",
      phone: false,
      remote: false,
      color: EMP_COLORS[Math.floor(Math.random() * EMP_COLORS.length)],
    }
  );
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(6,7,11,0.88)",
        backdropFilter: "blur(10px)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "rgba(14,15,22,0.99)",
          backdropFilter: "blur(32px)",
          border: `1px solid ${T.borderHi}`,
          borderRadius: 26,
          padding: 34,
          width: 390,
          display: "flex",
          flexDirection: "column",
          gap: 22,
          boxShadow:
            "0 28px 90px rgba(0,0,0,0.75), 0 0 80px rgba(200,160,100,0.06)",
          animation: "modalIn 0.28s cubic-bezier(.34,1.56,.64,1)",
        }}
      >
        {/* En-tête */}
        <div
          style={{ borderBottom: `1px solid ${T.border}`, paddingBottom: 18 }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 14,
                background: "rgba(200,160,100,0.1)",
                border: `1px solid ${T.borderMid}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 20,
              }}
            >
              {employee ? "✏" : "＋"}
            </div>
            <div>
              <div
                style={{
                  fontFamily: T.fontDisplay,
                  fontSize: 19,
                  fontWeight: 800,
                  color: T.text,
                  letterSpacing: -0.3,
                }}
              >
                {employee ? "Modifier le profil" : "Nouveau membre"}
              </div>
              <div
                style={{
                  fontFamily: T.fontBody,
                  fontSize: 12,
                  color: T.textMuted,
                  marginTop: 1,
                }}
              >
                Droits d'accès · Apparence
              </div>
            </div>
          </div>
        </div>

        {/* Nom */}
        <div>
          <label
            style={{
              fontFamily: T.fontBody,
              fontSize: 10,
              fontWeight: 700,
              color: T.textMuted,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              display: "block",
              marginBottom: 8,
            }}
          >
            Nom complet
          </label>
          <input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Prénom Nom"
            style={{
              width: "100%",
              background: "rgba(20,22,34,0.9)",
              border: `1px solid ${T.border}`,
              borderRadius: 13,
              padding: "12px 15px",
              color: T.text,
              fontSize: 13,
              fontFamily: T.fontBody,
              outline: "none",
              transition: "border 0.2s,box-shadow 0.2s",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = T.borderHi;
              e.target.style.boxShadow = `0 0 0 3px ${T.copperDim}`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = T.border;
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        {/* Couleur */}
        <div>
          <label
            style={{
              fontFamily: T.fontBody,
              fontSize: 10,
              fontWeight: 700,
              color: T.textMuted,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              display: "block",
              marginBottom: 10,
            }}
          >
            Couleur
          </label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {EMP_COLORS.map((c) => (
              <div
                key={c}
                onClick={() => set("color", c)}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 9,
                  background: c,
                  cursor: "pointer",
                  border:
                    form.color === c
                      ? `2px solid #f0ede6`
                      : `2px solid transparent`,
                  boxShadow: form.color === c ? `0 0 16px ${c}90` : "none",
                  transform: form.color === c ? "scale(1.18)" : "scale(1)",
                  transition: "all 0.18s",
                }}
              />
            ))}
          </div>
        </div>

        {/* Toggles */}
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {[
            {
              key: "phone",
              icon: "📞",
              label: "Téléphone professionnel",
              sub: "Accès autorisé au téléphone",
              colorOn: T.jade,
            },
            {
              key: "remote",
              icon: "⌂",
              label: "Télétravail",
              sub: "Éligible aux demi-journées remote",
              colorOn: T.copper,
            },
          ].map(({ key, icon, label, sub, colorOn }) => (
            <div
              key={key}
              onClick={() => set(key, !form[key])}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "13px 16px",
                background: "rgba(20,22,34,0.8)",
                borderRadius: 14,
                border: `1px solid ${form[key] ? colorOn + "35" : T.border}`,
                cursor: "pointer",
                transition: "border 0.22s,background 0.22s",
                boxShadow: form[key] ? `inset 0 1px 0 ${colorOn}15` : "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(26,28,42,0.9)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(20,22,34,0.8)";
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span
                  style={{
                    fontSize: 22,
                    filter: form[key] ? "none" : "saturate(0) opacity(0.35)",
                  }}
                >
                  {icon}
                </span>
                <div>
                  <div
                    style={{
                      fontFamily: T.fontBody,
                      fontSize: 12,
                      fontWeight: 700,
                      color: T.text,
                    }}
                  >
                    {label}
                  </div>
                  <div
                    style={{
                      fontFamily: T.fontBody,
                      fontSize: 10,
                      color: T.textMuted,
                      marginTop: 1,
                    }}
                  >
                    {form[key] ? "Activé ✓" : sub}
                  </div>
                </div>
              </div>
              <Toggler
                value={form[key]}
                onChange={() => {}}
                colorOn={colorOn}
              />
            </div>
          ))}
        </div>

        {/* Actions */}
        <div
          style={{
            display: "flex",
            gap: 10,
            marginTop: 2,
            borderTop: `1px solid ${T.border}`,
            paddingTop: 20,
          }}
        >
          <button
            onClick={onClose}
            style={{
              flex: 1,
              background: "rgba(20,22,34,0.8)",
              border: `1px solid ${T.border}`,
              borderRadius: 13,
              padding: "12px",
              color: T.textMuted,
              fontSize: 12,
              fontFamily: T.fontBody,
              cursor: "pointer",
              fontWeight: 600,
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = T.textSub;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = T.textMuted;
            }}
          >
            Annuler
          </button>
          <button
            onClick={() => {
              if (form.name.trim()) onSave(form);
            }}
            disabled={!form.name.trim()}
            style={{
              flex: 2,
              borderRadius: 13,
              padding: "12px",
              background: form.name.trim()
                ? "linear-gradient(135deg,#c8a064,#e8c88a)"
                : "rgba(20,22,34,0.5)",
              border: "none",
              color: form.name.trim() ? "#0a0b0f" : T.textMuted,
              fontSize: 12,
              fontFamily: T.fontDisplay,
              cursor: form.name.trim() ? "pointer" : "not-allowed",
              fontWeight: 800,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              boxShadow: form.name.trim()
                ? "0 4px 20px rgba(200,160,100,0.35)"
                : "none",
              transition: "all 0.2s",
            }}
          >
            {employee ? "Enregistrer" : "Créer le profil"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Agent IA Chat
function AgentChat({ onQuery, agentLog, onClear }) {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const logRef = useRef(null);
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [agentLog]);
  const send = async () => {
    if (!input.trim() || loading) return;
    const q = input;
    setInput("");
    setLoading(true);
    await onQuery(q);
    setLoading(false);
  };
  const QUICK = [
    "Analyse les absences",
    "Optimise le roulement",
    "Déséquilibre matin/soir ?",
    "Plus de remote ?",
    "Rapport mensuel RH",
  ];
  return (
    <Card style={{ padding: 24 }} glow={T.copper}>
      {/* Header agent */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 13,
          marginBottom: 18,
          paddingBottom: 16,
          borderBottom: `1px solid ${T.border}`,
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 14,
            flexShrink: 0,
            position: "relative",
            background:
              "linear-gradient(135deg,rgba(200,160,100,0.2),rgba(200,160,100,0.08))",
            border: `1px solid ${T.borderMid}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 0 24px rgba(200,160,100,0.2)`,
          }}
        >
          <span style={{ fontSize: 20 }}>◈</span>
          <div
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: T.jade,
              boxShadow: `0 0 8px ${T.jade}`,
            }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontFamily: T.fontDisplay,
              fontSize: 14,
              fontWeight: 800,
              color: T.text,
              letterSpacing: -0.2,
            }}
          >
            Agent IA · RH
          </div>
          <div
            style={{
              fontFamily: T.fontBody,
              fontSize: 10,
              color: T.textMuted,
              marginTop: 1,
            }}
          >
            Analyse · Optimisation · Alertes en direct
          </div>
        </div>
        {agentLog.length > 0 && (
          <button
            onClick={onClear}
            style={{
              background: "rgba(20,22,34,0.7)",
              border: `1px solid ${T.border}`,
              borderRadius: 8,
              padding: "4px 11px",
              color: T.textMuted,
              fontSize: 10,
              cursor: "pointer",
              fontFamily: T.fontBody,
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = T.textSub;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = T.textMuted;
            }}
          >
            Effacer
          </button>
        )}
      </div>

      {/* Log */}
      <div
        ref={logRef}
        style={{
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 14,
          minHeight: 130,
          maxHeight: 250,
        }}
      >
        {agentLog.length === 0 && (
          <div
            style={{ textAlign: "center", paddingTop: 28, paddingBottom: 10 }}
          >
            <div
              style={{ fontSize: 28, marginBottom: 10, filter: "opacity(0.3)" }}
            >
              ◈
            </div>
            <div
              style={{
                fontFamily: T.fontBody,
                fontSize: 12,
                color: T.textMuted,
              }}
            >
              Posez une question sur votre planning
            </div>
            <div
              style={{
                fontFamily: T.fontBody,
                fontSize: 10,
                color: T.textDim,
                marginTop: 4,
              }}
            >
              Contexte complet envoyé automatiquement à chaque requête
            </div>
          </div>
        )}
        {agentLog.map((e, i) => (
          <div key={i} style={{ animation: "fadeIn 0.3s ease" }}>
            <div
              style={{
                background: "rgba(20,22,34,0.7)",
                border: `1px solid ${T.border}`,
                borderRadius: "12px 12px 12px 3px",
                padding: "9px 14px",
                marginLeft: 52,
                fontFamily: T.fontBody,
                color: T.textMuted,
                fontSize: 11,
                fontStyle: "italic",
                lineHeight: 1.5,
              }}
            >
              {e.question}
            </div>
            <div
              style={{
                marginTop: 7,
                display: "flex",
                flexDirection: "column",
                gap: 5,
              }}
            >
              {e.result && (
                <div
                  style={{
                    background: `${T.copperDim}`,
                    border: `1px solid ${T.borderMid}`,
                    borderRadius: "3px 12px 12px 12px",
                    padding: "12px 15px",
                    marginRight: 52,
                    fontFamily: T.fontBody,
                    color: T.textSub,
                    fontSize: 11,
                    lineHeight: 1.65,
                  }}
                >
                  {e.result.message}
                </div>
              )}
              {e.result?.suggestion && (
                <div
                  style={{
                    background: T.jadeDim,
                    border: `1px solid rgba(78,205,196,0.2)`,
                    borderRadius: 10,
                    padding: "9px 14px",
                    marginRight: 52,
                    fontFamily: T.fontBody,
                    color: T.jade,
                    fontSize: 10,
                    lineHeight: 1.55,
                  }}
                >
                  ◆ {e.result.suggestion}
                </div>
              )}
              {e.result?.alert && (
                <div
                  style={{
                    background: T.roseDim,
                    border: `1px solid rgba(232,117,138,0.2)`,
                    borderRadius: 10,
                    padding: "9px 14px",
                    marginRight: 52,
                    fontFamily: T.fontBody,
                    color: T.rose,
                    fontSize: 10,
                    lineHeight: 1.55,
                  }}
                >
                  ⚠ {e.result.alert}
                </div>
              )}
              {e.error && (
                <div
                  style={{
                    color: T.rose,
                    fontSize: 10,
                    marginLeft: 4,
                    fontFamily: T.fontBody,
                  }}
                >
                  Erreur · {e.error}
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div
            style={{
              display: "flex",
              gap: 7,
              padding: "10px 15px",
              alignItems: "center",
            }}
          >
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: T.copper,
                  animation: "bounce 1.2s infinite",
                  animationDelay: `${i * 0.18}s`,
                }}
              />
            ))}
            <span
              style={{
                fontFamily: T.fontBody,
                fontSize: 10,
                color: T.textMuted,
                marginLeft: 5,
              }}
            >
              Analyse en cours…
            </span>
          </div>
        )}
      </div>

      {/* Input */}
      <div style={{ display: "flex", gap: 9, marginTop: 16 }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Posez une question RH…"
          style={{
            flex: 1,
            background: "rgba(20,22,34,0.85)",
            border: `1px solid ${T.border}`,
            borderRadius: 12,
            padding: "11px 15px",
            color: T.text,
            fontSize: 12,
            outline: "none",
            fontFamily: T.fontBody,
            transition: "border 0.2s,box-shadow 0.2s",
          }}
          onFocus={(e) => {
            e.target.style.borderColor = T.borderHi;
            e.target.style.boxShadow = `0 0 0 3px ${T.copperDim}`;
          }}
          onBlur={(e) => {
            e.target.style.borderColor = T.border;
            e.target.style.boxShadow = "none";
          }}
        />
        <button
          onClick={send}
          disabled={loading || !input.trim()}
          style={{
            background:
              loading || !input.trim()
                ? "rgba(20,22,34,0.6)"
                : "linear-gradient(135deg,#c8a064,#e8c88a)",
            border: `1px solid ${
              loading || !input.trim() ? T.border : "transparent"
            }`,
            borderRadius: 12,
            padding: "11px 16px",
            color: loading || !input.trim() ? T.textMuted : "#0a0b0f",
            cursor: loading || !input.trim() ? "not-allowed" : "pointer",
            fontSize: 17,
            transition: "all 0.2s",
            boxShadow:
              loading || !input.trim()
                ? "none"
                : "0 4px 18px rgba(200,160,100,0.4)",
            fontFamily: T.fontDisplay,
            fontWeight: 800,
          }}
        >
          ↑
        </button>
      </div>

      {/* Raccourcis */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
        {QUICK.map((q) => (
          <button
            key={q}
            onClick={() => setInput(q)}
            style={{
              background: T.copperDim,
              border: `1px solid ${T.borderMid}`,
              borderRadius: 20,
              padding: "4px 12px",
              color: T.textMuted,
              fontSize: 10,
              cursor: "pointer",
              fontFamily: T.fontBody,
              fontWeight: 500,
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `rgba(200,160,100,0.22)`;
              e.currentTarget.style.color = T.copperLight;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = T.copperDim;
              e.currentTarget.style.color = T.textMuted;
            }}
          >
            {q}
          </button>
        ))}
      </div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════
//  DONNÉES PAR DÉFAUT
// ═══════════════════════════════════════════════════════════════
const DEFAULT_EMPLOYEES = [
  { id: 1, name: "Alice Martin", color: "#34d399", remote: true, phone: true },
  { id: 2, name: "Bruno Leroy", color: "#60a5fa", remote: false, phone: false },
  {
    id: 3,
    name: "Camille Durand",
    color: "#f87171",
    remote: true,
    phone: true,
  },
  { id: 4, name: "David Chen", color: "#fbbf24", remote: false, phone: true },
  { id: 5, name: "Emma Petit", color: "#a78bfa", remote: true, phone: false },
  { id: 6, name: "Félix Moreau", color: "#fb7185", remote: false, phone: true },
  { id: 7, name: "Grace Blanc", color: "#22d3ee", remote: false, phone: false },
  { id: 8, name: "Hugo Roux", color: "#fb923c", remote: true, phone: true },
];

// ═══════════════════════════════════════════════════════════════
//  APP
// ═══════════════════════════════════════════════════════════════
export default function PlanningAgent() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [view, setView] = useState("planning");
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [employees, setEmployees] = useState(DEFAULT_EMPLOYEES);
  const [showEmpModal, setShowEmpModal] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [filters, setFilters] = useState({
    rotation: "aucune",
    changeFrequency: 2,
    absenceRate: 8,
    remoteManagement: true,
  });
  const [overrides, setOverrides] = useState({});
  const [agentLog, setAgentLog] = useState([]);
  const [agentHistory, setAgentHistory] = useState([]);
  const [retroSort, setRetroSort] = useState({ key: "name", dir: 1 });
  const [retroFilter, setRetroFilter] = useState("");
  const [globalAlert, setGlobalAlert] = useState(null);
  const [exportingPDF, setExportingPDF] = useState(false);

  const holidays = useMemo(() => getFrenchHolidays(year), [year]);
  const weeks = useMemo(
    () => getWeeksInMonth(year, month, holidays),
    [year, month, holidays]
  );
  const [schedule, setSchedule] = useState(() =>
    generateSchedule(
      DEFAULT_EMPLOYEES,
      {
        rotation: "aucune",
        changeFrequency: 2,
        absenceRate: 8,
        remoteManagement: true,
      },
      getWeeksInMonth(
        now.getFullYear(),
        now.getMonth(),
        getFrenchHolidays(now.getFullYear())
      ),
      {}
    )
  );

  const regen = useCallback(
    (f, w, emps, ov) => setSchedule(generateSchedule(emps, f, w, ov)),
    []
  );
  useEffect(() => {
    regen(filters, weeks, employees, overrides);
    setSelectedWeek(1);
  }, [year, month, filters, employees]);
  useEffect(() => {
    regen(filters, weeks, employees, overrides);
  }, [overrides]);
  useEffect(() => {
    const retro = buildRetroTable(schedule, employees, weeks);
    const high = Object.values(retro).filter(
      (r) => r.demi > 0 && r.absent / r.demi > 0.3
    );
    setGlobalAlert(
      high.length > 0
        ? `Taux d'absence critique (>30%) : ${high
            .map((r) => r.name)
            .join(", ")}`
        : null
    );
  }, [schedule]);

  const weekData = schedule[selectedWeek] || {};
  const curWeek = weeks.find((w) => w.week === selectedWeek);
  const retroTable = useMemo(
    () => buildRetroTable(schedule, employees, weeks),
    [schedule, employees, weeks]
  );
  const balanceScore = computeBalanceScore(schedule, selectedWeek);

  const handleHalfClick = (empId, dateStr, half, currentStatus) => {
    if (currentStatus === "holiday") return;
    setOverrides((ov) => ({
      ...ov,
      [`${empId}_${dateStr}_${half}`]: STATUS_CYCLE[currentStatus] || "present",
    }));
  };

  const handleAgentQuery = async (question) => {
    const retro = buildRetroTable(schedule, employees, weeks);
    const ctx = {
      période: `${MONTHS[month]} ${year}`,
      filtres: filters,
      équipe: employees.map((e) => ({
        nom: e.name,
        remote: e.remote,
        phone: e.phone,
      })),
      semaineActive: selectedWeek,
      scoreÉquilibre: balanceScore,
      résumé: weeks.map((w) => {
        const dj = Object.values(schedule[w.week] || {}).flatMap((a) =>
          a.flatMap((e) => [e.am, e.pm])
        );
        return {
          semaine: w.week,
          présents: dj.filter((s) => s === "present").length,
          remote: dj.filter((s) => s === "remote").length,
          absents: dj.filter((s) => s === "absent").length,
        };
      }),
      rétroactif: Object.values(retro).map((r) => ({
        nom: r.name,
        phone: r.phone,
        présent: r.present,
        télétravail: r.remote,
        absent: r.absent,
        taux:
          r.demi > 0 ? Math.round(((r.present + r.remote) / r.demi) * 100) : 0,
      })),
    };
    const userMsg = {
      role: "user",
      content: `Données:\n${JSON.stringify(
        ctx,
        null,
        2
      )}\n\nQuestion: ${question}`,
    };
    const newHistory = [...agentHistory, userMsg];
    try {
      const result = await callAIAgent(newHistory);
      setAgentHistory([
        ...newHistory,
        { role: "assistant", content: result.message },
      ]);
      setAgentLog((prev) => [...prev, { question, result }]);
      if (result.action === "regenerate")
        regen(filters, weeks, employees, overrides);
      if (result.action === "export") exportRetroCSV(retroTable, month, year);
    } catch (e) {
      setAgentLog((prev) => [...prev, { question, error: e.message }]);
    }
  };

  const saveEmployee = (form) => {
    if (editEmp)
      setEmployees((prev) =>
        prev.map((e) => (e.id === editEmp.id ? { ...form, id: editEmp.id } : e))
      );
    else {
      const newId = Math.max(0, ...employees.map((e) => e.id)) + 1;
      setEmployees((prev) => [...prev, { ...form, id: newId }]);
    }
    setShowEmpModal(false);
    setEditEmp(null);
  };

  const sortedRetro = useMemo(() => {
    return Object.values(retroTable)
      .filter((r) => r.name.toLowerCase().includes(retroFilter.toLowerCase()))
      .sort((a, b) => {
        const va =
          retroSort.key === "rate"
            ? a.demi > 0
              ? (a.present + a.remote) / a.demi
              : 0
            : a[retroSort.key] || "";
        const vb =
          retroSort.key === "rate"
            ? b.demi > 0
              ? (b.present + b.remote) / b.demi
              : 0
            : b[retroSort.key] || "";
        return typeof va === "string"
          ? va.localeCompare(vb) * retroSort.dir
          : (va - vb) * retroSort.dir;
      });
  }, [retroTable, retroSort, retroFilter]);

  const toggleSort = (k) =>
    setRetroSort((s) => ({ key: k, dir: s.key === k ? -s.dir : 1 }));
  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const handleExportPlanningPDF = async () => {
    setExportingPDF("planning");
    try {
      await exportPlanningPDF({
        employees,
        schedule,
        weeks,
        filters,
        month,
        year,
        MONTHS,
        DAYS_SHORT,
      });
    } finally {
      setExportingPDF(false);
    }
  };
  const handleExportRetroPDF = async () => {
    setExportingPDF("retro");
    try {
      await exportRetroPDF({
        retroTable,
        schedule,
        weeks,
        month,
        year,
        employees,
        MONTHS,
      });
    } finally {
      setExportingPDF(false);
    }
  };

  const allHalves = Object.values(weekData).flatMap((a) =>
    a.flatMap((e) => [e.am, e.pm])
  );
  const wStats = {
    present: allHalves.filter((s) => s === "present").length,
    remote: allHalves.filter((s) => s === "remote").length,
    absent: allHalves.filter((s) => s === "absent").length,
    holiday: allHalves.filter((s) => s === "holiday").length,
    manual: Object.values(weekData)
      .flat()
      .filter((e) => e.amManual || e.pmManual).length,
  };

  // ─── RENDER ───────────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: "100vh",
        background: T.bg,
        fontFamily: T.fontBody,
        color: T.text,
        padding: "26px 30px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* ── CSS ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,300;12..96,400;12..96,600;12..96,700;12..96,800&family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:3px;height:3px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:rgba(200,160,100,0.25);border-radius:2px}
        @keyframes bounce{0%,80%,100%{transform:translateY(0)}40%{transform:translateY(-5px)}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        @keyframes slideIn{from{opacity:0;transform:translateX(-12px)}to{opacity:1;transform:translateX(0)}}
        @keyframes modalIn{from{opacity:0;transform:scale(0.9) translateY(20px)}to{opacity:1;transform:scale(1) translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes ember{0%,100%{transform:translate(0,0) scale(1);opacity:0.6}50%{transform:translate(20px,-30px) scale(1.12);opacity:0.8}}
        @keyframes ember2{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(-25px,18px) scale(0.9)}}
        @keyframes alertPulse{0%,100%{box-shadow:0 0 0 rgba(232,117,138,0)}50%{box-shadow:0 0 24px rgba(232,117,138,0.15)}}
        @keyframes stagger{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        .card{animation:stagger 0.4s cubic-bezier(.34,1.16,.64,1) both}
        .card:nth-child(1){animation-delay:0.05s}
        .card:nth-child(2){animation-delay:0.1s}
        .card:nth-child(3){animation-delay:0.15s}
        .card:nth-child(4){animation-delay:0.2s}
        .row-hover:hover td{background:rgba(200,160,100,0.03)!important}
        th.sortable{cursor:pointer;user-select:none;transition:color 0.15s}
        th.sortable:hover{color:${T.copperLight}!important}
        input[type=range]{accent-color:${T.copper};width:100%;height:3px;cursor:pointer}
        select{appearance:none;-webkit-appearance:none}
        select option{background:#0e0f16;color:${T.text}}
      `}</style>

      {/* ── FOND ATMOSPHÉRIQUE ── */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          overflow: "hidden",
          zIndex: 0,
        }}
      >
        {/* Taches de lumière chaudes */}
        <div
          style={{
            position: "absolute",
            top: "-20%",
            left: "-8%",
            width: 700,
            height: 700,
            borderRadius: "50%",
            background:
              "radial-gradient(circle,rgba(200,160,100,0.055) 0%,transparent 65%)",
            animation: "ember 18s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-15%",
            right: "-5%",
            width: 600,
            height: 600,
            borderRadius: "50%",
            background:
              "radial-gradient(circle,rgba(78,205,196,0.04) 0%,transparent 65%)",
            animation: "ember2 22s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "45%",
            right: "15%",
            width: 350,
            height: 350,
            borderRadius: "50%",
            background:
              "radial-gradient(circle,rgba(200,160,100,0.035) 0%,transparent 65%)",
            animation: "ember 14s ease-in-out infinite 5s",
          }}
        />
        {/* Grille fine */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `linear-gradient(${T.border} 1px,transparent 1px),linear-gradient(90deg,${T.border} 1px,transparent 1px)`,
            backgroundSize: "48px 48px",
            opacity: 0.35,
          }}
        />
      </div>

      <div style={{ position: "relative", zIndex: 1 }}>
        {/* ── ALERTE GLOBALE ── */}
        {globalAlert && (
          <div
            style={{
              background: "rgba(232,117,138,0.07)",
              border: "1px solid rgba(232,117,138,0.25)",
              borderRadius: 14,
              padding: "11px 18px",
              marginBottom: 20,
              display: "flex",
              alignItems: "center",
              gap: 11,
              fontFamily: T.fontBody,
              animation: "alertPulse 3s infinite",
            }}
          >
            <div
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: T.rose,
                boxShadow: `0 0 8px ${T.rose}`,
                flexShrink: 0,
              }}
            />
            <span
              style={{ fontSize: 12, color: T.rose, fontWeight: 500, flex: 1 }}
            >
              {globalAlert}
            </span>
            <button
              onClick={() => setGlobalAlert(null)}
              style={{
                background: "none",
                border: "none",
                color: `${T.rose}80`,
                cursor: "pointer",
                fontSize: 19,
                lineHeight: 1,
                fontFamily: T.fontDisplay,
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* ══ HEADER ══════════════════════════════════════════════ */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 30,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            {/* Logo */}
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 16,
                background:
                  "linear-gradient(135deg,rgba(200,160,100,0.18),rgba(200,160,100,0.06))",
                border: `1px solid ${T.borderMid}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 0 30px rgba(200,160,100,0.14),inset 0 1px 0 rgba(200,160,100,0.15)`,
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontFamily: T.fontDisplay,
                  fontSize: 24,
                  color: T.copper,
                  fontWeight: 800,
                  lineHeight: 1,
                }}
              >
                P
              </span>
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                <h1
                  style={{
                    fontFamily: T.fontDisplay,
                    fontSize: 26,
                    fontWeight: 800,
                    color: T.text,
                    letterSpacing: -0.8,
                    lineHeight: 1,
                  }}
                >
                  Planning <span style={{ color: T.copper }}>Agent</span>
                </h1>
                <span
                  style={{
                    fontFamily: T.fontDisplay,
                    fontSize: 10,
                    fontWeight: 700,
                    color: T.copper,
                    background: T.copperDim,
                    border: `1px solid ${T.borderMid}`,
                    borderRadius: 20,
                    padding: "2px 9px",
                    letterSpacing: "0.1em",
                  }}
                >
                  V3
                </span>
              </div>
              <div
                style={{
                  fontFamily: T.fontBody,
                  fontSize: 12,
                  color: T.textMuted,
                  marginTop: 4,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span>{employees.length} membres</span>
                <span style={{ color: T.textDim }}>·</span>
                <span style={{ fontWeight: 500, color: T.textSub }}>
                  {MONTHS[month]} {year}
                </span>
                <span style={{ color: T.textDim }}>·</span>
                <span>Grille M/S</span>
                <span style={{ color: T.textDim }}>·</span>
                <span
                  style={{
                    color:
                      balanceScore >= 80
                        ? T.jade
                        : balanceScore >= 60
                        ? T.amber
                        : T.rose,
                    fontWeight: 700,
                  }}
                >
                  Équilibre {balanceScore}/100
                </span>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <GhostBtn
              onClick={() => setView("planning")}
              active={view === "planning"}
            >
              <span style={{ marginRight: 6 }}>◧</span> Planning
            </GhostBtn>
            <GhostBtn
              onClick={() => setView("retro")}
              active={view === "retro"}
            >
              <span style={{ marginRight: 6 }}>◫</span> Rétroactif
            </GhostBtn>
          </div>
        </div>

        {/* ══ LAYOUT 2 COLONNES ══ */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "276px 1fr",
            gap: 18,
            alignItems: "start",
          }}
        >
          {/* ── SIDEBAR ────────────────────────────────────────── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Période */}
            <Card style={{ padding: 20 }} hover>
              <SLabel color={T.jade}>Période</SLabel>
              <div style={{ display: "flex", gap: 8 }}>
                <select
                  value={month}
                  onChange={(e) => setMonth(+e.target.value)}
                  style={{
                    flex: 1,
                    background: "rgba(18,20,30,0.9)",
                    border: `1px solid ${T.border}`,
                    borderRadius: 10,
                    padding: "9px 12px",
                    color: T.text,
                    fontSize: 12,
                    fontFamily: T.fontBody,
                    cursor: "pointer",
                    transition: "border 0.2s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = T.borderMid)}
                  onBlur={(e) => (e.target.style.borderColor = T.border)}
                >
                  {MONTHS.map((m, i) => (
                    <option key={i} value={i}>
                      {m}
                    </option>
                  ))}
                </select>
                <select
                  value={year}
                  onChange={(e) => setYear(+e.target.value)}
                  style={{
                    width: 82,
                    background: "rgba(18,20,30,0.9)",
                    border: `1px solid ${T.border}`,
                    borderRadius: 10,
                    padding: "9px 10px",
                    color: T.text,
                    fontSize: 12,
                    fontFamily: T.fontBody,
                    cursor: "pointer",
                  }}
                >
                  {[2024, 2025, 2026, 2027].map((y) => (
                    <option key={y}>{y}</option>
                  ))}
                </select>
              </div>
            </Card>

            {/* Équipe */}
            <Card style={{ padding: 20 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 14,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <div
                      style={{
                        width: 2,
                        height: 10,
                        background: T.copperLight,
                        borderRadius: 1,
                      }}
                    />
                    <div
                      style={{
                        width: 2,
                        height: 4,
                        background: `${T.copperLight}50`,
                        borderRadius: 1,
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontFamily: T.fontDisplay,
                      fontSize: 10,
                      fontWeight: 700,
                      color: T.copperLight,
                      letterSpacing: "0.15em",
                      textTransform: "uppercase",
                    }}
                  >
                    Équipe
                  </span>
                  <span
                    style={{
                      fontFamily: T.fontDisplay,
                      fontSize: 10,
                      fontWeight: 800,
                      color: T.copper,
                      background: T.copperDim,
                      border: `1px solid ${T.borderMid}`,
                      borderRadius: 20,
                      padding: "1px 8px",
                    }}
                  >
                    {employees.length}
                  </span>
                </div>
                <button
                  onClick={() => {
                    setEditEmp(null);
                    setShowEmpModal(true);
                  }}
                  style={{
                    background: T.copperDim,
                    border: `1px solid ${T.borderMid}`,
                    borderRadius: 8,
                    padding: "5px 12px",
                    color: T.copper,
                    fontSize: 11,
                    cursor: "pointer",
                    fontFamily: T.fontDisplay,
                    fontWeight: 700,
                    transition: "all 0.2s",
                    letterSpacing: "0.05em",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = `rgba(200,160,100,0.22)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = T.copperDim;
                  }}
                >
                  + Membre
                </button>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                  maxHeight: 210,
                  overflowY: "auto",
                }}
              >
                {employees.map((emp, ei) => (
                  <div
                    key={emp.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 9,
                      padding: "8px 10px",
                      borderRadius: 12,
                      background: "rgba(18,20,30,0.6)",
                      border: `1px solid transparent`,
                      transition: "all 0.18s",
                      cursor: "default",
                      animation: `slideIn 0.3s ${ei * 0.04}s both`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = `${emp.color}40`;
                      e.currentTarget.style.background = `${emp.color}08`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "transparent";
                      e.currentTarget.style.background = "rgba(18,20,30,0.6)";
                    }}
                  >
                    <Avatar name={emp.name} color={emp.color} size={28} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontFamily: T.fontBody,
                          fontSize: 11,
                          fontWeight: 700,
                          color: T.text,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {emp.name.split(" ")[0]}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          marginTop: 2,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 11,
                            filter: emp.phone
                              ? "none"
                              : "grayscale(1) opacity(0.25)",
                          }}
                          title={emp.phone ? "Accès tél." : "Pas de tél."}
                        >
                          📞
                        </span>
                        {emp.remote && (
                          <span
                            style={{
                              fontFamily: T.fontBody,
                              fontSize: 8,
                              color: T.copper,
                              fontWeight: 700,
                              background: T.copperDim,
                              borderRadius: 4,
                              padding: "1px 5px",
                            }}
                          >
                            TT
                          </span>
                        )}
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: 2,
                        opacity: 0,
                        transition: "opacity 0.15s",
                      }}
                      className="emp-actions"
                    >
                      <button
                        onClick={() => {
                          setEditEmp(emp);
                          setShowEmpModal(true);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          color: T.textMuted,
                          cursor: "pointer",
                          fontSize: 12,
                          padding: "3px",
                          borderRadius: 5,
                          transition: "color 0.15s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.color = T.copperLight)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.color = T.textMuted)
                        }
                      >
                        ✎
                      </button>
                      {employees.length > 2 && (
                        <button
                          onClick={() =>
                            setEmployees((p) =>
                              p.filter((e) => e.id !== emp.id)
                            )
                          }
                          style={{
                            background: "none",
                            border: "none",
                            color: T.textMuted,
                            cursor: "pointer",
                            fontSize: 11,
                            padding: "3px",
                            borderRadius: 5,
                            transition: "color 0.15s",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.color = T.rose)
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.color = T.textMuted)
                          }
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <style>{`.emp-actions{opacity:0!important}.row-emp:hover .emp-actions{opacity:1!important}`}</style>
            </Card>

            {/* Filtres */}
            <Card style={{ padding: 20 }}>
              <SLabel>5 Filtres IA</SLabel>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                {/* ① Roulement */}
                <div>
                  <div
                    style={{
                      fontFamily: T.fontBody,
                      fontSize: 10,
                      fontWeight: 700,
                      color: T.textMuted,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      marginBottom: 8,
                    }}
                  >
                    ① Roulement
                  </div>
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 4 }}
                  >
                    {[
                      ["aucune", "Aucun roulement"],
                      ["matin-soir", "Matin / Soir"],
                      ["semaine", "Par semaine"],
                    ].map(([val, label]) => (
                      <div
                        key={val}
                        onClick={() => setF("rotation", val)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 9,
                          padding: "8px 11px",
                          borderRadius: 10,
                          cursor: "pointer",
                          background:
                            filters.rotation === val
                              ? T.copperDim
                              : "transparent",
                          border: `1px solid ${
                            filters.rotation === val
                              ? T.borderMid
                              : "transparent"
                          }`,
                          transition: "all 0.15s",
                        }}
                      >
                        <div
                          style={{
                            width: 13,
                            height: 13,
                            borderRadius: 4,
                            flexShrink: 0,
                            background:
                              filters.rotation === val
                                ? T.copper
                                : "transparent",
                            border: `1.5px solid ${
                              filters.rotation === val ? T.copper : T.textDim
                            }`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow:
                              filters.rotation === val
                                ? `0 0 8px ${T.copper}50`
                                : "none",
                            transition: "all 0.15s",
                          }}
                        >
                          {filters.rotation === val && (
                            <span
                              style={{
                                color: "#0a0b0f",
                                fontSize: 8,
                                fontWeight: 900,
                                lineHeight: 1,
                              }}
                            >
                              ✓
                            </span>
                          )}
                        </div>
                        <span
                          style={{
                            fontFamily: T.fontBody,
                            fontSize: 11,
                            color:
                              filters.rotation === val ? T.text : T.textMuted,
                            fontWeight: filters.rotation === val ? 600 : 400,
                          }}
                        >
                          {label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Divider */}
                <div
                  style={{
                    height: 1,
                    background: `linear-gradient(90deg,transparent,${T.border},transparent)`,
                  }}
                />

                {/* ② Fréquence */}
                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      marginBottom: 8,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: T.fontBody,
                        fontSize: 10,
                        fontWeight: 700,
                        color: T.textMuted,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                      }}
                    >
                      ② Fréquence rotation
                    </span>
                    <span
                      style={{
                        fontFamily: T.fontDisplay,
                        fontSize: 14,
                        fontWeight: 800,
                        color: T.copper,
                      }}
                    >
                      /{filters.changeFrequency}j
                    </span>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={5}
                    value={filters.changeFrequency}
                    onChange={(e) => setF("changeFrequency", +e.target.value)}
                  />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontFamily: T.fontBody,
                      color: T.textDim,
                      fontSize: 8,
                      marginTop: 3,
                    }}
                  >
                    <span>1 jour</span>
                    <span>5 jours</span>
                  </div>
                </div>

                {/* ③ Absences */}
                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      marginBottom: 8,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: T.fontBody,
                        fontSize: 10,
                        fontWeight: 700,
                        color: T.textMuted,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                      }}
                    >
                      ③ Taux d'absence
                    </span>
                    <span
                      style={{
                        fontFamily: T.fontDisplay,
                        fontSize: 14,
                        fontWeight: 800,
                        color:
                          filters.absenceRate > 20
                            ? T.rose
                            : filters.absenceRate > 10
                            ? T.amber
                            : T.copper,
                      }}
                    >
                      {filters.absenceRate}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={30}
                    value={filters.absenceRate}
                    onChange={(e) => setF("absenceRate", +e.target.value)}
                  />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontFamily: T.fontBody,
                      color: T.textDim,
                      fontSize: 8,
                      marginTop: 3,
                    }}
                  >
                    <span>0%</span>
                    <span>30%</span>
                  </div>
                </div>

                {/* ④ Télétravail */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 13px",
                    background: "rgba(18,20,30,0.6)",
                    borderRadius: 12,
                    border: `1px solid ${
                      filters.remoteManagement ? T.borderMid : T.border
                    }`,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontFamily: T.fontBody,
                        fontSize: 11,
                        fontWeight: 700,
                        color: T.text,
                      }}
                    >
                      ④ Télétravail auto
                    </div>
                    <div
                      style={{
                        fontFamily: T.fontBody,
                        fontSize: 9,
                        color: T.textMuted,
                        marginTop: 1,
                      }}
                    >
                      Affecté en demi-journée M ou S
                    </div>
                  </div>
                  <Toggler
                    value={filters.remoteManagement}
                    onChange={() =>
                      setF("remoteManagement", !filters.remoteManagement)
                    }
                    colorOn={T.copper}
                  />
                </div>

                {/* ⑤ Fériés */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "9px 13px",
                    background: "rgba(240,184,64,0.05)",
                    border: `1px solid rgba(240,184,64,0.15)`,
                    borderRadius: 12,
                  }}
                >
                  <div
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: T.amber,
                      flexShrink: 0,
                      boxShadow: `0 0 6px ${T.amber}`,
                    }}
                  />
                  <div>
                    <div
                      style={{
                        fontFamily: T.fontBody,
                        fontSize: 10,
                        fontWeight: 700,
                        color: T.amber,
                      }}
                    >
                      ⑤ Jours fériés FR {year}
                    </div>
                    <div
                      style={{
                        fontFamily: T.fontBody,
                        fontSize: 9,
                        color: T.textMuted,
                        marginTop: 1,
                      }}
                    >
                      Exclusion automatique active
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 7,
                    paddingTop: 4,
                  }}
                >
                  <CopperBtn
                    onClick={() => regen(filters, weeks, employees, overrides)}
                    style={{ justifyContent: "center", padding: "10px" }}
                  >
                    ↻ Régénérer
                  </CopperBtn>
                  {Object.keys(overrides).length > 0 && (
                    <button
                      onClick={() => setOverrides({})}
                      style={{
                        background: "rgba(18,20,30,0.6)",
                        border: `1px solid ${T.border}`,
                        borderRadius: 11,
                        padding: "9px",
                        color: T.textMuted,
                        cursor: "pointer",
                        fontSize: 10,
                        fontFamily: T.fontBody,
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = T.rose;
                        e.currentTarget.style.borderColor =
                          "rgba(232,117,138,0.3)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = T.textMuted;
                        e.currentTarget.style.borderColor = T.border;
                      }}
                    >
                      ✕ Effacer {Object.keys(overrides).length} modification
                      {Object.keys(overrides).length > 1 ? "s" : ""}
                    </button>
                  )}
                </div>
              </div>
            </Card>

            {/* Légende compacte */}
            <Card style={{ padding: 16 }}>
              <SLabel color={T.textMuted}>Légende</SLabel>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 6,
                }}
              >
                {[
                  { s: T.statusPresent, sym: "◆", label: "Présent" },
                  { s: T.statusRemote, sym: "⌂", label: "Télétravail" },
                  { s: T.statusAbsent, sym: "✕", label: "Absent" },
                  { s: T.statusHoliday, sym: "✦", label: "Férié" },
                ].map(({ s, sym, label }) => (
                  <div
                    key={label}
                    style={{ display: "flex", alignItems: "center", gap: 7 }}
                  >
                    <div
                      style={{
                        width: 26,
                        height: 22,
                        borderRadius: 6,
                        background: s.bg,
                        border: `1px solid ${s.border}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 8,
                          fontWeight: 900,
                          color: s.text,
                          fontFamily: T.fontDisplay,
                        }}
                      >
                        {sym}
                      </span>
                    </div>
                    <span
                      style={{
                        fontFamily: T.fontBody,
                        fontSize: 10,
                        color: T.textMuted,
                        fontWeight: 500,
                      }}
                    >
                      {label}
                    </span>
                  </div>
                ))}
              </div>
              <div
                style={{
                  marginTop: 12,
                  padding: "8px 10px",
                  background: "rgba(200,160,100,0.05)",
                  borderRadius: 9,
                  border: `1px solid ${T.border}`,
                }}
              >
                <div
                  style={{
                    fontFamily: T.fontBody,
                    fontSize: 9,
                    color: T.textMuted,
                    lineHeight: 1.65,
                  }}
                >
                  Chaque jour ={" "}
                  <span style={{ color: T.copper, fontWeight: 700 }}>
                    Matin [M]
                  </span>{" "}
                  +{" "}
                  <span style={{ color: T.copperLight, fontWeight: 700 }}>
                    Soir [S]
                  </span>
                  <br />
                  Clic sur une demi-journée pour modifier
                  <br />
                  <span style={{ color: T.jade }}>📞</span> visible = tél.
                  autorisé
                </div>
              </div>
            </Card>
          </div>

          {/* ── ZONE PRINCIPALE ──────────────────────────────────── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {view === "planning" ? (
              <>
                {/* Sélecteur semaines */}
                <div style={{ display: "flex", gap: 8 }}>
                  {weeks.map((w) => {
                    const all = Object.values(schedule[w.week] || {}).flatMap(
                      (a) => a.flatMap((e) => [e.am, e.pm])
                    );
                    const absPct =
                      all.length > 0
                        ? Math.round(
                            (all.filter((s) => s === "absent").length /
                              all.length) *
                              100
                          )
                        : 0;
                    const isSel = selectedWeek === w.week;
                    return (
                      <button
                        key={w.week}
                        onClick={() => setSelectedWeek(w.week)}
                        style={{
                          flex: 1,
                          background: isSel
                            ? T.copperDim
                            : "rgba(16,18,26,0.7)",
                          border: `1px solid ${isSel ? T.borderHi : T.border}`,
                          borderRadius: 12,
                          padding: "10px 8px",
                          fontFamily: T.fontDisplay,
                          color: isSel ? T.copperLight : T.textMuted,
                          fontSize: 11,
                          cursor: "pointer",
                          fontWeight: isSel ? 800 : 500,
                          transition: "all 0.2s",
                          boxShadow: isSel
                            ? `0 4px 20px rgba(200,160,100,0.12)`
                            : "none",
                        }}
                      >
                        <div>S{w.week}</div>
                        <div
                          style={{
                            fontFamily: T.fontBody,
                            fontSize: 8,
                            marginTop: 2,
                            opacity: 0.6,
                            fontWeight: 400,
                          }}
                        >
                          {w.start.toLocaleDateString("fr-FR", {
                            day: "numeric",
                            month: "short",
                          })}
                        </div>
                        {absPct > 20 && (
                          <div
                            style={{
                              fontFamily: T.fontBody,
                              fontSize: 7,
                              color: T.rose,
                              marginTop: 2,
                              fontWeight: 700,
                            }}
                          >
                            ▲ {absPct}%
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Grille planning */}
                <Card style={{ padding: 22 }} glow={T.copper}>
                  {/* Entête grille */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 16,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontFamily: T.fontDisplay,
                          fontSize: 16,
                          fontWeight: 800,
                          color: T.text,
                          letterSpacing: -0.3,
                        }}
                      >
                        Semaine {selectedWeek}
                        <span
                          style={{
                            fontFamily: T.fontBody,
                            fontSize: 12,
                            fontWeight: 400,
                            color: T.textMuted,
                            marginLeft: 10,
                            letterSpacing: 0,
                          }}
                        >
                          {MONTHS[month]} {year}
                        </span>
                      </div>
                      <div
                        style={{
                          fontFamily: T.fontBody,
                          fontSize: 10,
                          color: T.textMuted,
                          marginTop: 4,
                        }}
                      >
                        Colonnes divisées{" "}
                        <span style={{ color: T.copper, fontWeight: 600 }}>
                          Matin
                        </span>{" "}
                        ·{" "}
                        <span style={{ color: T.copperLight, fontWeight: 600 }}>
                          Soir
                        </span>{" "}
                        — clic pour modifier
                      </div>
                    </div>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <BalancePill score={balanceScore} />
                      {wStats.manual > 0 && (
                        <div
                          style={{
                            fontFamily: T.fontBody,
                            fontSize: 10,
                            color: T.copper,
                            background: T.copperDim,
                            border: `1px solid ${T.borderMid}`,
                            borderRadius: 20,
                            padding: "3px 10px",
                            fontWeight: 600,
                          }}
                        >
                          {wStats.manual} modif.
                        </div>
                      )}
                      <CopperBtn
                        onClick={handleExportPlanningPDF}
                        loading={exportingPDF === "planning"}
                      >
                        ↓ PDF
                      </CopperBtn>
                    </div>
                  </div>

                  {/* Table */}
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        minWidth: 540,
                      }}
                    >
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                          <th
                            style={{
                              padding: "8px 12px",
                              textAlign: "left",
                              fontFamily: T.fontBody,
                              color: T.textMuted,
                              fontSize: 9,
                              fontWeight: 700,
                              letterSpacing: "0.1em",
                              textTransform: "uppercase",
                              width: 148,
                            }}
                          >
                            Membre
                          </th>
                          {curWeek?.days.map(({ date, isHoliday }, i) => (
                            <th
                              key={i}
                              style={{
                                padding: "8px 6px",
                                textAlign: "center",
                                color: isHoliday ? T.amber : T.textMuted,
                                fontSize: 9,
                                fontWeight: 700,
                                borderBottom: "none",
                              }}
                            >
                              <div
                                style={{
                                  fontFamily: T.fontDisplay,
                                  fontWeight: 800,
                                  color: isHoliday ? T.amber : T.textSub,
                                }}
                              >
                                {DAYS_SHORT[i]}
                              </div>
                              <div
                                style={{
                                  fontFamily: T.fontBody,
                                  fontSize: 7,
                                  marginTop: 1,
                                  opacity: 0.6,
                                }}
                              >
                                {date.getDate()}/{date.getMonth() + 1}
                              </div>
                              {isHoliday && (
                                <div
                                  style={{
                                    fontFamily: T.fontBody,
                                    fontSize: 6,
                                    color: T.amber,
                                    marginTop: 1,
                                    fontWeight: 700,
                                  }}
                                >
                                  Férié
                                </div>
                              )}
                              {!isHoliday && (
                                <div
                                  style={{
                                    display: "flex",
                                    gap: 3,
                                    justifyContent: "center",
                                    marginTop: 4,
                                  }}
                                >
                                  <span
                                    style={{
                                      background: T.copperDim,
                                      color: T.copper,
                                      fontSize: 6,
                                      padding: "1px 5px",
                                      borderRadius: 3,
                                      fontFamily: T.fontDisplay,
                                      fontWeight: 700,
                                      letterSpacing: "0.05em",
                                    }}
                                  >
                                    M
                                  </span>
                                  <span
                                    style={{
                                      background: "rgba(200,160,100,0.07)",
                                      color: `${T.copper}80`,
                                      fontSize: 6,
                                      padding: "1px 5px",
                                      borderRadius: 3,
                                      fontFamily: T.fontDisplay,
                                      fontWeight: 700,
                                    }}
                                  >
                                    S
                                  </span>
                                </div>
                              )}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {employees.map((emp, ei) => (
                          <tr
                            key={emp.id}
                            className="row-hover"
                            style={{
                              borderBottom: `1px solid rgba(200,160,100,0.06)`,
                              transition: "background 0.12s",
                            }}
                          >
                            <td style={{ padding: "9px 12px" }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 9,
                                }}
                              >
                                <Avatar
                                  name={emp.name}
                                  color={emp.color}
                                  size={30}
                                />
                                <div>
                                  <div
                                    style={{
                                      fontFamily: T.fontBody,
                                      fontSize: 11,
                                      fontWeight: 700,
                                      color: T.text,
                                    }}
                                  >
                                    {emp.name.split(" ")[0]}
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 5,
                                      marginTop: 2,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 11,
                                        filter: emp.phone
                                          ? "none"
                                          : "grayscale(1) opacity(0.2)",
                                      }}
                                      title={
                                        emp.phone
                                          ? "Accès tél. ✓"
                                          : "Pas de tél."
                                      }
                                    >
                                      📞
                                    </span>
                                    <span
                                      style={{
                                        fontFamily: T.fontBody,
                                        fontSize: 8,
                                        color: emp.phone ? T.jade : T.textDim,
                                        fontWeight: 700,
                                      }}
                                    >
                                      {emp.phone ? "Tél. ✓" : ""}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </td>
                            {curWeek?.days.map(({ dateStr }, di) => {
                              const entry = (weekData[dateStr] || []).find(
                                (e) => e.id === emp.id
                              );
                              return (
                                <td
                                  key={di}
                                  style={{
                                    padding: "7px 6px",
                                    textAlign: "center",
                                  }}
                                >
                                  <DayCell
                                    entry={entry}
                                    onClickHalf={(half) =>
                                      handleHalfClick(
                                        emp.id,
                                        dateStr,
                                        half,
                                        entry[half]
                                      )
                                    }
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                {/* Stats 4 blocs */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4,1fr)",
                    gap: 12,
                  }}
                  className="card"
                >
                  <StatBlock
                    icon="◆"
                    label="Demi-j présents"
                    value={wStats.present}
                    color={T.jade}
                    note="sur le site"
                  />
                  <StatBlock
                    icon="⌂"
                    label="Demi-j télétravail"
                    value={wStats.remote}
                    color={T.copper}
                    note="remote"
                  />
                  <StatBlock
                    icon="✕"
                    label="Demi-j absents"
                    value={wStats.absent}
                    color={T.rose}
                    note="non travaillés"
                  />
                  <StatBlock
                    icon="✦"
                    label="Demi-j fériés"
                    value={wStats.holiday}
                    color={T.amber}
                    note={`${MONTHS[month].slice(0, 3)}. ${year}`}
                  />
                </div>
              </>
            ) : (
              /* ══ RÉTROACTIF ══════════════════════════════════════ */
              <Card style={{ padding: 24 }} glow={T.jade}>
                {/* En-tête */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 18,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontFamily: T.fontDisplay,
                        fontSize: 16,
                        fontWeight: 800,
                        color: T.text,
                        letterSpacing: -0.3,
                      }}
                    >
                      Rétroactif mensuel
                      <span
                        style={{
                          fontFamily: T.fontBody,
                          fontSize: 12,
                          fontWeight: 400,
                          color: T.textMuted,
                          marginLeft: 10,
                        }}
                      >
                        {MONTHS[month]} {year}
                      </span>
                    </div>
                    <div
                      style={{
                        fontFamily: T.fontBody,
                        fontSize: 10,
                        color: T.textMuted,
                        marginTop: 4,
                      }}
                    >
                      Unité · demi-journées · Cliquer les en-têtes pour trier ·
                      Camemberts = répartition par semaine
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => exportRetroCSV(retroTable, month, year)}
                      style={{
                        background: "rgba(78,205,196,0.08)",
                        border: `1px solid rgba(78,205,196,0.2)`,
                        borderRadius: 10,
                        padding: "8px 14px",
                        color: T.jade,
                        fontSize: 11,
                        cursor: "pointer",
                        fontFamily: T.fontDisplay,
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background =
                          "rgba(78,205,196,0.14)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background =
                          "rgba(78,205,196,0.08)";
                      }}
                    >
                      ↓ CSV
                    </button>
                    <CopperBtn
                      onClick={handleExportRetroPDF}
                      loading={exportingPDF === "retro"}
                    >
                      ↓ PDF
                    </CopperBtn>
                  </div>
                </div>

                {/* Recherche */}
                <div style={{ position: "relative", marginBottom: 14 }}>
                  <span
                    style={{
                      position: "absolute",
                      left: 13,
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: 13,
                      color: T.textMuted,
                      pointerEvents: "none",
                    }}
                  >
                    ◎
                  </span>
                  <input
                    value={retroFilter}
                    onChange={(e) => setRetroFilter(e.target.value)}
                    placeholder="Filtrer par nom…"
                    style={{
                      width: "100%",
                      background: "rgba(18,20,30,0.85)",
                      border: `1px solid ${T.border}`,
                      borderRadius: 11,
                      padding: "9px 12px 9px 34px",
                      color: T.text,
                      fontSize: 12,
                      fontFamily: T.fontBody,
                      outline: "none",
                      transition: "border 0.2s",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = T.borderHi;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = T.border;
                    }}
                  />
                </div>

                {/* Table */}
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                        {[
                          ["name", "Membre"],
                          ["phone", "📞"],
                          ["demi", "Demi-j."],
                          ["present", "Présent"],
                          ["remote", "Tél."],
                          ["absent", "Absent"],
                          ["rate", "Taux"],
                        ].map(([k, l]) => (
                          <th
                            key={k}
                            className="sortable"
                            onClick={() => toggleSort(k)}
                            style={{
                              padding: "9px 12px",
                              textAlign: k === "name" ? "left" : "center",
                              fontFamily: T.fontDisplay,
                              color:
                                retroSort.key === k
                                  ? T.copperLight
                                  : T.textMuted,
                              fontSize: 9,
                              fontWeight: 700,
                              letterSpacing: "0.1em",
                              textTransform: "uppercase",
                            }}
                          >
                            {l}
                            {retroSort.key === k
                              ? retroSort.dir === 1
                                ? " ↑"
                                : " ↓"
                              : ""}
                          </th>
                        ))}
                        {weeks.map((w) => (
                          <th
                            key={w.week}
                            style={{
                              padding: "9px 8px",
                              textAlign: "center",
                              fontFamily: T.fontDisplay,
                              color: T.textDim,
                              fontSize: 8,
                              fontWeight: 700,
                            }}
                          >
                            S{w.week}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRetro.map((row, ri) => {
                        const rate =
                          row.demi > 0
                            ? Math.round(
                                ((row.present + row.remote) / row.demi) * 100
                              )
                            : 0;
                        const rc =
                          rate >= 80 ? T.jade : rate >= 60 ? T.amber : T.rose;
                        return (
                          <tr
                            key={row.id}
                            className="row-hover"
                            style={{
                              borderBottom: `1px solid rgba(200,160,100,0.05)`,
                              animation: `stagger 0.3s ${ri * 0.04}s both`,
                            }}
                          >
                            <td style={{ padding: "11px 12px" }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 9,
                                }}
                              >
                                <Avatar
                                  name={row.name}
                                  color={row.color}
                                  size={28}
                                />
                                <span
                                  style={{
                                    fontFamily: T.fontBody,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: T.text,
                                  }}
                                >
                                  {row.name}
                                </span>
                              </div>
                            </td>
                            <td
                              style={{
                                padding: "11px 12px",
                                textAlign: "center",
                              }}
                            >
                              <Tip
                                text={
                                  row.phone
                                    ? "Accès téléphone autorisé"
                                    : "Sans accès téléphone"
                                }
                              >
                                <span
                                  style={{
                                    fontSize: 15,
                                    filter: row.phone
                                      ? "none"
                                      : "grayscale(1) opacity(0.18)",
                                    cursor: "help",
                                  }}
                                >
                                  📞
                                </span>
                              </Tip>
                            </td>
                            <td
                              style={{
                                padding: "11px 12px",
                                textAlign: "center",
                                fontFamily: T.fontDisplay,
                                fontSize: 14,
                                fontWeight: 800,
                                color: T.text,
                              }}
                            >
                              {row.demi}
                            </td>
                            <td
                              style={{
                                padding: "11px 12px",
                                textAlign: "center",
                              }}
                            >
                              <span
                                style={{
                                  background: T.jadeDim,
                                  border: `1px solid rgba(78,205,196,0.2)`,
                                  padding: "3px 10px",
                                  borderRadius: 20,
                                  fontFamily: T.fontDisplay,
                                  fontSize: 12,
                                  fontWeight: 800,
                                  color: T.jade,
                                }}
                              >
                                {row.present}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "11px 12px",
                                textAlign: "center",
                              }}
                            >
                              <span
                                style={{
                                  background: T.copperDim,
                                  border: `1px solid ${T.borderMid}`,
                                  padding: "3px 10px",
                                  borderRadius: 20,
                                  fontFamily: T.fontDisplay,
                                  fontSize: 12,
                                  fontWeight: 800,
                                  color: T.copper,
                                }}
                              >
                                {row.remote}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "11px 12px",
                                textAlign: "center",
                              }}
                            >
                              <span
                                style={{
                                  background:
                                    row.absent > 0 ? T.roseDim : "transparent",
                                  border: `1px solid ${
                                    row.absent > 0
                                      ? "rgba(232,117,138,0.2)"
                                      : "transparent"
                                  }`,
                                  padding: "3px 10px",
                                  borderRadius: 20,
                                  fontFamily: T.fontDisplay,
                                  fontSize: 12,
                                  fontWeight: 800,
                                  color: row.absent > 0 ? T.rose : T.textDim,
                                }}
                              >
                                {row.absent}
                              </span>
                            </td>
                            <td style={{ padding: "11px 12px" }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <div
                                  style={{
                                    flex: 1,
                                    height: 4,
                                    background: "rgba(20,22,34,0.9)",
                                    borderRadius: 10,
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    style={{
                                      height: "100%",
                                      borderRadius: 10,
                                      width: `${rate}%`,
                                      background: `linear-gradient(90deg,${rc},${rc}88)`,
                                      transition:
                                        "width 0.6s cubic-bezier(.34,1.16,.64,1)",
                                    }}
                                  />
                                </div>
                                <span
                                  style={{
                                    fontFamily: T.fontDisplay,
                                    fontSize: 11,
                                    fontWeight: 800,
                                    color: rc,
                                    minWidth: 34,
                                  }}
                                >
                                  {rate}%
                                </span>
                              </div>
                            </td>
                            {weeks.map((w) => {
                              const wb = row.byWeek[w.week] || {
                                p: 0,
                                r: 0,
                                a: 0,
                              };
                              const tot = wb.p + wb.r + wb.a;
                              return (
                                <td
                                  key={w.week}
                                  style={{
                                    padding: "11px 8px",
                                    textAlign: "center",
                                  }}
                                >
                                  <Tip
                                    text={`S${w.week} · P=${wb.p} TT=${wb.r} A=${wb.a}`}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 2,
                                        cursor: "pointer",
                                      }}
                                      onClick={() => {
                                        setView("planning");
                                        setSelectedWeek(w.week);
                                      }}
                                    >
                                      {tot > 0 ? (
                                        <div
                                          style={{
                                            width: 20,
                                            height: 20,
                                            borderRadius: "50%",
                                            background: `conic-gradient(${
                                              T.jade
                                            } 0% ${(wb.p / tot) * 100}%, ${
                                              T.copper
                                            } ${(wb.p / tot) * 100}% ${
                                              ((wb.p + wb.r) / tot) * 100
                                            }%, ${T.rose} ${
                                              ((wb.p + wb.r) / tot) * 100
                                            }% 100%)`,
                                            boxShadow:
                                              "0 0 6px rgba(0,0,0,0.4)",
                                          }}
                                        />
                                      ) : (
                                        <div
                                          style={{
                                            width: 20,
                                            height: 20,
                                            borderRadius: "50%",
                                            background: "rgba(20,22,34,0.8)",
                                          }}
                                        />
                                      )}
                                      <span
                                        style={{
                                          fontFamily: T.fontBody,
                                          fontSize: 7,
                                          color: T.textDim,
                                          fontWeight: 500,
                                        }}
                                      >
                                        {tot}dj
                                      </span>
                                    </div>
                                  </Tip>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr
                        style={{
                          borderTop: `1px solid ${T.borderMid}`,
                          background: `${T.copperDim}`,
                        }}
                      >
                        <td
                          colSpan={2}
                          style={{
                            padding: "11px 12px",
                            fontFamily: T.fontDisplay,
                            fontSize: 10,
                            fontWeight: 800,
                            color: T.copper,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                          }}
                        >
                          Totaux
                        </td>
                        <td
                          style={{
                            padding: "11px 12px",
                            textAlign: "center",
                            fontFamily: T.fontDisplay,
                            fontSize: 14,
                            fontWeight: 800,
                            color: T.text,
                          }}
                        >
                          {sortedRetro.reduce((s, r) => s + r.demi, 0)}
                        </td>
                        <td
                          style={{
                            padding: "11px 12px",
                            textAlign: "center",
                            fontFamily: T.fontDisplay,
                            fontSize: 14,
                            fontWeight: 800,
                            color: T.jade,
                          }}
                        >
                          {sortedRetro.reduce((s, r) => s + r.present, 0)}
                        </td>
                        <td
                          style={{
                            padding: "11px 12px",
                            textAlign: "center",
                            fontFamily: T.fontDisplay,
                            fontSize: 14,
                            fontWeight: 800,
                            color: T.copper,
                          }}
                        >
                          {sortedRetro.reduce((s, r) => s + r.remote, 0)}
                        </td>
                        <td
                          style={{
                            padding: "11px 12px",
                            textAlign: "center",
                            fontFamily: T.fontDisplay,
                            fontSize: 14,
                            fontWeight: 800,
                            color: T.rose,
                          }}
                        >
                          {sortedRetro.reduce((s, r) => s + r.absent, 0)}
                        </td>
                        <td style={{ padding: "11px 12px" }}>
                          <span
                            style={{
                              fontFamily: T.fontBody,
                              fontSize: 11,
                              color: T.textMuted,
                              fontWeight: 600,
                            }}
                          >
                            Moy.{" "}
                            {Math.round(
                              (sortedRetro.reduce(
                                (s, r) =>
                                  s +
                                  (r.demi > 0
                                    ? (r.present + r.remote) / r.demi
                                    : 0),
                                0
                              ) /
                                Math.max(sortedRetro.length, 1)) *
                                100
                            )}
                            %
                          </span>
                        </td>
                        {weeks.map((w) => (
                          <td key={w.week} />
                        ))}
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Graphe barres */}
                <div
                  style={{
                    marginTop: 24,
                    paddingTop: 18,
                    borderTop: `1px solid ${T.border}`,
                  }}
                >
                  <div
                    style={{
                      fontFamily: T.fontDisplay,
                      fontSize: 9,
                      fontWeight: 700,
                      color: T.textMuted,
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      marginBottom: 12,
                    }}
                  >
                    Présence par semaine
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 9,
                      alignItems: "flex-end",
                      height: 68,
                    }}
                  >
                    {weeks.map((wk) => {
                      const all = Object.values(schedule[wk.week] || {})
                        .flatMap((a) => a.flatMap((e) => [e.am, e.pm]))
                        .filter((s) => s !== "holiday");
                      const pct =
                        all.length > 0
                          ? (all.filter((s) => s !== "absent").length /
                              all.length) *
                            100
                          : 0;
                      const color =
                        pct >= 80 ? T.jade : pct >= 60 ? T.amber : T.rose;
                      const isSel = selectedWeek === wk.week;
                      return (
                        <Tip
                          key={wk.week}
                          text={`Sem ${wk.week} · ${Math.round(pct)}% présents`}
                        >
                          <div
                            onClick={() => {
                              setView("planning");
                              setSelectedWeek(wk.week);
                            }}
                            style={{
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 4,
                              cursor: "pointer",
                            }}
                          >
                            <div
                              style={{
                                fontFamily: T.fontDisplay,
                                fontSize: 8,
                                fontWeight: 800,
                                color: `${color}cc`,
                              }}
                            >
                              {Math.round(pct)}%
                            </div>
                            <div
                              style={{
                                width: "100%",
                                borderRadius: 6,
                                height: Math.max(6, pct * 0.54),
                                background: `linear-gradient(180deg,${color} 0%,${color}44 100%)`,
                                minHeight: 6,
                                transition:
                                  "height 0.5s cubic-bezier(.34,1.16,.64,1)",
                                boxShadow: isSel
                                  ? `0 0 16px ${color}50`
                                  : "none",
                              }}
                            />
                            <div
                              style={{
                                fontFamily: T.fontDisplay,
                                fontSize: 8,
                                fontWeight: isSel ? 800 : 500,
                                color: isSel ? color : T.textDim,
                              }}
                            >
                              S{wk.week}
                            </div>
                          </div>
                        </Tip>
                      );
                    })}
                  </div>
                </div>
              </Card>
            )}

            <AgentChat
              onQuery={handleAgentQuery}
              agentLog={agentLog}
              onClear={() => {
                setAgentLog([]);
                setAgentHistory([]);
              }}
            />
          </div>
        </div>
      </div>

      {showEmpModal && (
        <EmployeeModal
          employee={editEmp}
          onSave={saveEmployee}
          onClose={() => {
            setShowEmpModal(false);
            setEditEmp(null);
          }}
        />
      )}
    </div>
  );
}
